import React, { useEffect, useState, useRef, useMemo } from "react";
import { Layout, Row, Col, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FetchLandingPageAction } from "../../redux/action/LandingPageAction";
import { getUserClaims } from "../../utils/auth/unified-auth";
import { useAuth } from "../../utils/auth/useAuth";
import { LoadingSpinner, Switcher, Icon, Button, CardV2, Card, DataTable, Modal, AccordionItemV2, Tooltip } from "pepsico-ds";
import { SkeletonList } from "../../components/Skeleton/SkeletonList";
import "../../components/Layout/Layout.scss";
import CommodityPageHeader from "../../components/Header/CommodityPageHeader";
import { call7DaysNpsLogic, formatNumberWithCommas, splitValueWithUnit, getToDateOptionsWithMaxDate } from "../../utils/utils";
import NewCategoryForum from '../../assets/NewCategoryForums.svg';
import "./CategoryForums.scss";
import { useLoginEffects } from "../WalkmeTour/UseLoginEffects";
import ForumKnowMoreModalicon from '../../assets/ForumKnowMoreModalicon.svg'
import ForumContractBoardKnowMoreIcon from '../../assets/ForumContractBoardKnowMoreIcon.svg'
import ShowMoreArrow from '../../assets/ShowMoreArrow.svg'
import ShowLessArrow from '../../assets/ShowLessArrow.svg'
import {
    FetchCategoryForumConfigAction,
    FetchCategoryForumTableAction,
    FetchCategoryForumYearToDateDateAction,
    FetchCategoryForumForumsByCategoryAction,
    FetchCategoryForumBalanceOfTheYearAction,
    FetchCategoryForumSummaryAction,
    FetchCategoryForumContractBoardsDetailsAction,
    FetchCategoryForumActionTrackerFiltersAction,
    FetchCategoryForumActionTrackerHeaderDataAction,
    FetchCategoryForumActionTrackerTabularDataAction,
    FetchCategoryForumActionTrackerChartDataAction,
    FetchCategoryForumOutcomeSummaryFiltersAction,
    FetchCategoryForumOutcomeSummaryTabularDataAction
} from "../../redux/action/CategoryForumAction";
import DropdownWithIcons from '../../components/Modal/DropdownWithIcons';
import { localStoreKeys } from '../../constants/localStoreKeys';
import { setKeyValue } from "../../redux/reducer/localDataSlice";
import CategoryForumsBarChart from "../../components/Charts/CategoryForums/CategoryForumsBarChart";
import NoData from "../../components/NoData/NoData";
import SpendDonutChart from "../../components/Charts/SpendAnalyticsChart/SpendDonutChart";
import CommonTable from "../../components/Table/CommonTable";
import { PHKPICard } from "../../components/Cards/PHKPICard";
import MultiSelectDropdown from "../../components/Dropdown/MultiSelectDropdown";
import SingleSelectDropdown from "../../components/Dropdown/SingleSelectDropdown";

import { useToast } from '../../components/Toast/ToastContext';
import Vector from '../../assets/Vector.png'
import reset_icon from "../../assets/reset_icon.svg";
import { exportRowsToExcel } from '../../utils/excelExport';
import YourCategoryForums from "./YourCategoryForums";

const { Content } = Layout;
/* istanbul ignore next */
function parseMonthYearKey(monthYear) {
    const parts = monthYear.split(' '); // ["Jan", "-", "2026"]
    const monthAbbr = parts[0].toLowerCase();
    const yearShort = String(parts[2]).slice(2);
    return { key: `${monthAbbr}${yearShort}`, header: `${parts[0].toUpperCase()} ${parts[2]}` };
}
/* istanbul ignore next */
function convertForumTableData(apiData) {
    const contractBoardRow = { id: '1', forumType: 'Contract Board' };
    const strategyCouncilRow = { id: '2', forumType: 'Strategy Council' };

    (apiData || []).forEach((item) => {
        const { key } = parseMonthYearKey(item.monthYear);
        contractBoardRow[key] = item.contractBoard || [];
        strategyCouncilRow[key] = item.strategyCouncil || [];
    });

    return [contractBoardRow, strategyCouncilRow];
}
/* istanbul ignore next */
function generateColumnsFromApiData(apiData, legendItems) {
    const cellDataFn = createCellDataFunction(legendItems);
    const monthKeys = (apiData || []).map((item) => parseMonthYearKey(item.monthYear).key);
    const columns = [
        {
            columnKey: 'forumType',
            header: 'FORUM TYPE',
            columnAlignment: 'left',
            size: 90,
            minSize: 270,
            maxSize: 340,
            cellData: cellDataFn,
        },
        ...(apiData || []).map((item) => {
            const { key, header } = parseMonthYearKey(item.monthYear);
            return {
                columnKey: key,
                header,
                columnAlignment: 'left',
                size: 90,
                minSize: 270,
                maxSize: 340,
                cellData: cellDataFn,
            };
        }),
    ];
    return { monthKeys, columns };
}
/* istanbul ignore next */
function getEventColors(item, forumType, legends) {
    const structured = toStructuredItem(item);
    if (structured && structured.l1Category) {
        const key = String(structured.l1Category).toLowerCase().trim();
        const legend = (legends || []).find(legend => String(legend.label).toLowerCase() === key);
        if (legend) return { bg: legend.color, stroke: legend.strokeColor };
    }
    return forumType === 'Strategy Council'
        ? { bg: '#D8E7FB', stroke: '#006FCF' }
        : { bg: '#FFE0BA', stroke: '#F5A623' };
}
/* istanbul ignore next */
function parseEventString(str) {
    const SEP = ' – ';
    const parts = str.split(SEP);
    if (parts.length < 3) return null;

    const date = parts[0].trim();
    const l1Category = parts[1].trim();
    let sector = '', l2Category = '', presenter = '';

    if (parts.length === 3) {
        l2Category = parts[2].trim();
    } else if (parts.length === 4) {
        l2Category = parts[2].trim();
        presenter = parts[3].trim();
    } else {
        l2Category = parts[2].trim();
        sector = parts[3].trim();
        presenter = parts[4].trim();
    }

    return { date, l1Category, l2Category, sector, presenter };
}
/* istanbul ignore next */
function toStructuredItem(item) {
    if (item && typeof item === 'object') return item;
    if (typeof item === 'string') return parseEventString(item);
    return null;
}
/* istanbul ignore next */
function getEventDisplayText(item) {
    const structured = toStructuredItem(item);
    if (structured) {
        return [structured.date, structured.l2Category, structured.sector, structured.presenter]
            .filter(Boolean)
            .join(' – ');
    }
    return String(item);
}

const MAX_VISIBLE_ITEMS = 4;

/* istanbul ignore next */
function buildCategoryGroupsFromCategoryObjects(categories = []) {

    const MainCategories = categories?.map(item => ({
        ...item,
        name: item.name === "C&I" ? "Commodities & Ingredients" : item.name === "A&M" ? "Advertisement & Marketing" : item.name
    }));
    const categoryMap = (MainCategories || []).reduce((acc, categoryItem) => {
        const category = String(categoryItem?.name || '').trim();
        const subcategories = Array.isArray(categoryItem?.subcategories) ? categoryItem.subcategories : [];

        if (!category) {
            return acc;
        }

        if (!acc[category]) {
            acc[category] = new Set();
        }

        subcategories.forEach((subCategory) => {
            const subCategoryName = String(subCategory || '').trim();
            if (subCategoryName) {
                acc[category].add(subCategoryName);
            }
        });

        return acc;
    }, {});

    return Object.entries(categoryMap).map(([category, subCategorySet]) => {
        const subCategories = Array.from(subCategorySet);
        return {
            category,
            subCategories,
            count: subCategories.length || 1,
        };
    });
}

/* istanbul ignore next */
function ForumCell({ items, forumType, legendItems }) {
    const [expanded, setExpanded] = useState(false);

    const filteredItems = items.filter(
        (item) => item !== null && item !== undefined && getEventDisplayText(item).trim() !== ''
    );

    if (filteredItems.length === 0) {
        return (
            <div title="-" style={{ display: 'flex', flexDirection: 'column', minHeight: '6rem', justifyContent: 'center' }}>
                <span style={{ margin: '5px', paddingLeft: '2px', color: '#2D2D2D' }}>-</span>
            </div>
        );
    }

    const visibleItems = expanded ? filteredItems : filteredItems.slice(0, MAX_VISIBLE_ITEMS);
    const hiddenCount = filteredItems.length - MAX_VISIBLE_ITEMS;

    return (
        <div style={{
            padding: '0 var(--Primitives-Spacing-spacing-8, 8px) 0 0',
        }}>
            {visibleItems.map((item, index) => {
                const structured = toStructuredItem(item);
                const displayText = getEventDisplayText(item);
                const { bg: bgColor, stroke: strokeColor } = getEventColors(item, forumType, legendItems);
                const line1 = structured
                    ? [structured.date, structured.l2Category].filter(Boolean).join(' - ')
                    : displayText;
                const line2 = structured
                    ? [structured.sector, structured.presenter].filter(Boolean).join(' - ')
                    : null;
                const tooltipContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-8)' }}>
                        <span style={{ fontWeight: '600' }}>{line1}</span>
                        {line2 && <span>{line2}</span>}
                    </div>
                );
                return (
                    <div key={index} style={{ width: '100%', marginTop: '5px' }}>
                        <Tooltip direction="top-middle" text={tooltipContent}>
                            <div
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    padding: 'var(--spacing-4) var(--spacing-12)',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 'var(--spacing-16)',
                                    borderRadius: 'var(--border-radius-container-large)',
                                    border: `1px solid ${strokeColor}`,
                                    background: bgColor,
                                    margin: '0 5px 5px',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Content group */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    alignSelf: 'stretch',
                                    minWidth: 0,
                                }}>
                                    {/* Line 1 */}
                                    <div style={{
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        color: '#1A1A1A',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}>
                                        {line1}
                                    </div>
                                    {/* Line 2 */}
                                    {line2 && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#555555',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                            {line2}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                );
            })}
            {!expanded && hiddenCount > 0 && (
                <span
                    className="forum-cell-expand-link"
                    onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(true); }}
                >
                    <img src={ShowMoreArrow} alt="show more" style={{ width: '12px', height: '12px' }} />{hiddenCount} more
                </span>
            )}
            {expanded && hiddenCount > 0 && (
                <span
                    className="forum-cell-expand-link"
                    onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(false); }}
                >
                    <img src={ShowLessArrow} alt="show less" style={{ width: '12px', height: '12px' }} />See less
                </span>
            )}
        </div>
    );
}
/* istanbul ignore next */
function createCellDataFunction(legendItems) {
    return (ctx) => {
        const colId = ctx.column.id;
        const value = ctx.row.original[colId];

        if (colId !== 'forumType') {
            const items = Array.isArray(value) ? value : (value !== null && value !== undefined ? [value] : []);
            return <ForumCell items={items} forumType={ctx.row.original.forumType} legendItems={legendItems} />;
        }
        const forumTypeValue = String(value || '');
        return (
            <div style={{
                width: '100%', height: '100%', fontSize: '16px', fontWeight: '600', padding: '10px 12px', color: '#2D2D2D',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-start', lineHeight: '1.5', minHeight: '6rem'
            }}>
                <span>{forumTypeValue}</span>
            </div>
        );
    };
}

/* istanbul ignore next */
function formatPresenters(presenters) {
    if (typeof presenters === 'string') {
        const value = presenters.trim();
        return value ? [value] : [];
    }
    return [];
}

/* istanbul ignore next */
function getOutcomeSummaryDropdownOptions(data) {
    return Array.isArray(data) ? data.map((item, index) => ({ displayText: item, id: index + 1 })) : [];
}

/* istanbul ignore next */
function getCategoryL0BySector(data, selectedSector) {
    const result = [];
    let idCounter = 1;

    if (Array.isArray(data) && selectedSector) {
        const sectorItem = data.find(item => item.name === selectedSector);

        if (sectorItem && Array.isArray(sectorItem.l0)) {
            sectorItem.l0.forEach(l0Item => {
                if (l0Item && l0Item.name) {
                    result.push({ displayText: l0Item.name, id: idCounter, isBadge: false });
                    idCounter++;
                }
            });
        }
    }

    return result;
}

/* istanbul ignore next */
function getCategoryL1BySectorAndL0(data, selectedSector, selectedCategoryL0) {
    const result = [];
    let idCounter = 1;

    if (Array.isArray(data) && selectedSector && selectedCategoryL0) {
        const sectorItem = data.find(item => item.name === selectedSector);

        if (sectorItem && Array.isArray(sectorItem.l0)) {
            const l0Item = sectorItem.l0.find(l0 => l0.name === selectedCategoryL0);

            if (l0Item && Array.isArray(l0Item.l1)) {
                l0Item.l1.forEach(l1Item => {
                    if (l1Item && l1Item.name) {
                        result.push({ displayText: l1Item.name, id: idCounter, isBadge: false });
                        idCounter++;
                    }
                });
            }
        }
    }

    return result;
}

/* istanbul ignore next */
function getPresenterBySectorL0AndL1(data, selectedSector, selectedCategoryL0, selectedCategoryL1) {
    if (Array.isArray(data) && selectedSector && selectedCategoryL0 && selectedCategoryL1) {
        const sectorItem = data.find(item => item.name === selectedSector);
        const l0Item = sectorItem?.l0?.find(l0 => l0.name === selectedCategoryL0);
        const l1Item = l0Item?.l1?.find(l1 => l1.name === selectedCategoryL1);

        return getOutcomeSummaryDropdownOptions(l1Item?.presenter);
    }

    return [];
}

/* istanbul ignore next */
function mergeUniqueOptions(optionGroups) {
    const seen = new Set();
    const merged = [];
    let idCounter = 1;

    optionGroups.flat().forEach((option) => {
        if (option && option.displayText && !seen.has(option.displayText)) {
            seen.add(option.displayText);
            merged.push({ ...option, id: idCounter });
            idCounter++;
        }
    });

    return merged;
}

/* istanbul ignore next */
function createPresenterCellRenderer() {
    return (ctx) => {
        const presenterList = formatPresenters(ctx.row.original.presenters);
        if (presenterList.length === 0) {
            return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', }}>
                {presenterList.map((presenter, idx) => (
                    <div key={idx} style={{ width: '100%', padding: '4px 0' }}>
                        {presenter}
                    </div>
                ))}
            </div>
        );
    };
}

/* istanbul ignore next */
function createDefaultDashCellRenderer(columnKey) {
    return (ctx) => {
        const rawValue = ctx?.row?.original?.[columnKey];
        const value = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
        return value ? <span>{rawValue}</span> : <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
    };
}

/* istanbul ignore next */
function createFolderNameCellRenderer() {
    return (ctx) => {
        const folderName = ctx?.row?.original?.folderName;
        const folderUrl = ctx?.row?.original?.folderUrl;
        const value = folderName === null || folderName === undefined ? '' : String(folderName).trim();

        if (!value) {
            return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
        }

        if (!folderUrl) {
            return <span>{value}</span>;
        }

        return (
            <a
                href={folderUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#034895', textDecoration: 'none' }}
            >
                {value}
            </a>
        );
    };
}

/* istanbul ignore next */
function createLicenseToOperateCellRenderer() {
    return (ctx) => {
        const rawValue = String(ctx.row.original.licenseToOperate || '').trim().toLowerCase();
        const isYes = rawValue === 'yes';
        const isNo = rawValue === 'no';

        if (!isYes && !isNo) {
            return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
        }

        const icon = isYes ? '✓' : '✕';
        const label = isYes ? 'Yes' : 'No';

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '5px',
                    background: isYes ? '#98E492' : '#CDCDCD',
                    color: '#111111',
                    // fontSize: '18px',
                    fontWeight: 500,
                    lineHeight: 1,
                }}
            >
                <span style={{ fontSize: '18px', lineHeight: 1 }}>{icon}</span>
                <span>{label}</span>
            </span>
        );
    };
}

/* istanbul ignore next */
function createLongTextTooltipCellRenderer(columnKey, tooltipThreshold = 200) {
    return (ctx) => {
        const value = String(ctx.row.original[columnKey] || '');
        const shouldShowTooltip = value.length > tooltipThreshold;
        const truncatedValue = shouldShowTooltip ? `${value.slice(0, tooltipThreshold)}...` : value;

        if (!value) {
            return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
        }

        const cellContent = (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {truncatedValue}
            </div>
        );

        if (!shouldShowTooltip) {
            return cellContent;
        }

        return (
            <Tooltip direction="left" text={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</div>}>
                {cellContent}
            </Tooltip>
        );
    };
}

/* istanbul ignore next */
function createStatusLegendCellRenderer(statusLegends = []) {
    return (ctx) => {
        const rawValue = ctx?.row?.original?.status;
        const value = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();

        if (!value) {
            return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
        }

        const matchedLegend = (statusLegends || []).find((legend) => String(legend?.label || '').toLowerCase() === value.toLowerCase());
        if (!matchedLegend) {
            return <span>{rawValue}</span>;
        }

        return (
            <span
                style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '5px',
                    background: matchedLegend?.color || '#EFEFEF',
                    border: `1px solid ${matchedLegend?.strokeColor || '#A2A2A2'}`,
                    color: '#111111',
                    lineHeight: 1.2,
                }}
            >
                {rawValue}
            </span>
        );
    };
}

const dummyContractBoardsOutcomeSummaryData = {
    "sectors": [
        {
            "name": "OneNA",
            "l0": [
                {
                    "name": "C&I",
                    "l1": [
                        {
                            "name": "Corn Based Sweetners",
                            "presenter": [
                                "Oytun Sepin"
                            ]
                        }
                    ]
                },
                {
                    "name": "Indirects",
                    "l1": [
                        {
                            "name": "MRO",
                            "presenter": [
                                "MalloryLamb"
                            ]
                        }
                    ]
                }
            ]
        },
        {
            "name": "Russia",
            "l0": [
                {
                    "name": "C&I",
                    "l1": [
                        {
                            "name": "Sugar",
                            "presenter": [
                                "Ceren"
                            ]
                        }
                    ]
                }
            ]
        }
    ],
    "dateInfo": {
        "maxDate": "2026-08-11",
        "maxYearMonth": "Aug 26",
        "minDate": "2026-03-17",
        "minYearMonth": "Mar 26",
        "defaultMax": "Aug 26",
        "defaultMin": "Mar 26"
    }
}

const contractBoardsOutcomesSummaryLegendItems = [
    { label: "More than MDO achieved", color: "#E8F0FB", strokeColor: "#60A3FC" },
    { label: "MDO achieved", color: "#DFF8DC", strokeColor: "#2DC22C" },
    { label: "Between MDO and LDO achieved", color: "#F3FBF2", strokeColor: "#B2ECAD" },
    { label: "LDO achieved", color: "#F7F2CB", strokeColor: "#C79B1B" },
    { label: "Less than LDO achieved", color: "#FEECD6", strokeColor: "#E78710" },
    { label: "Not achieved", color: "#FFE5DE", strokeColor: "#FA6443" },
];

/* istanbul ignore next */
function createLegendPillCellRenderer(columnKey, statusKey, legendItems) {
    return (ctx) => {
        const rawValue = ctx?.row?.original?.[columnKey];

        if (rawValue === null || rawValue === '') {
            return <div style={{ width: '100%', textAlign: 'center' }}>Not Applicable</div>;
        }

        const rawStatus = ctx?.row?.original?.[statusKey];
        const status = rawStatus === null || rawStatus === undefined ? '' : String(rawStatus).trim();
        const matchedLegend = (legendItems || []).find((legend) => String(legend?.label || '').toLowerCase() === status.toLowerCase());
        if (!matchedLegend) {
            return <span>{rawValue}</span>;
        }

        return (
            <span
                style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '5px',
                    background: matchedLegend?.color || '#EFEFEF',
                    border: `1px solid ${matchedLegend?.strokeColor || '#A2A2A2'}`,
                    color: '#111111',
                    lineHeight: 1.2,
                }}
            >
                {rawValue}
            </span>
        );
    };
}

/* istanbul ignore next */
function renderLegendMarkup(legendItems, kpiName) {
    if (!legendItems || legendItems.length === 0) {
        return null;
    }
    return (
        <div className={kpiName == "cf-calendar" ? "calendar-legend-wrapper" : "cb-calendar-legend-wrapper"}>
            {legendItems.map((item, i) => (
                <span key={i} className="calendar-legend-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M5 0.5C7.48528 0.5 9.5 2.51472 9.5 5C9.5 7.48528 7.48528 9.5 5 9.5C2.51472 9.5 0.5 7.48528 0.5 5C0.5 2.51472 2.51472 0.5 5 0.5Z" fill={item.color} stroke={item.strokeColor || item.color} strokeWidth="1" />
                    </svg>
                    <span className="calendar-legend-label">{item.label}</span>
                </span>
            ))}
        </div>
    );
}

/* istanbul ignore next */
function renderFooterSection(staticOwnersData, feedbackFormUrl) {
    return (
        <div className="spend-modal-manage-spend" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: '100%' }}>
            {
                staticOwnersData?.length > 0 &&
                <div style={{ width: '100%' }}>Please reach out to {staticOwnersData?.map((contact, index) => (
                    <React.Fragment key={index}>
                        <a href={`mailto:${contact.emailId}`} style={{ color: "#005cbc", textDecoration: "None" }}>{contact.emailId}</a>
                        {index < staticOwnersData?.length - 1 && ', '}
                    </React.Fragment>

                ))} for any query.</div>
            }
            <div style={{ width: '100%' }}>Please fill in <a href={feedbackFormUrl} target="_blank" style={{ color: "#005cbc", textDecoration: "None" }}>this form</a> for any feedback related to Procurement Hub.</div>
        </div>
    );
}


/* istanbul ignore next */
function buildContractBoardsTableColumns(showFolderDetails) {
    return [
        ...(showFolderDetails ? [{ columnKey: "folderName", header: "Folder", size: 160, minSize: 120, maxSize: 240, cellData: createFolderNameCellRenderer() }] : []),
        { columnKey: "date", header: (
            <>
                Date
                <br />
                (YYYY-MM-DD)
            </>
        ), size: 90, minSize: 90, maxSize: 140, cellData: createDefaultDashCellRenderer("date") },
        { columnKey: "category", header: "Category", size: 120, minSize: 90, maxSize: 180, cellData: createDefaultDashCellRenderer("category") },
        { columnKey: "sector", header: "Sector", size: 110, minSize: 90, maxSize: 160, cellData: createDefaultDashCellRenderer("sector") },
        { columnKey: "presenters", header: "Presenter(s)", size: 120, minSize: 90, maxSize: 180, cellData: createPresenterCellRenderer() },
        { columnKey: "spend", header: "Spend ($MN)", size: 100, minSize: 90, maxSize: 140, cellData: createDefaultDashCellRenderer("spend") },
        { columnKey: "bidTechnique", header: "Bid Technique", size: 110, minSize: 90, maxSize: 160, cellData: createDefaultDashCellRenderer("bidTechnique") },
        { columnKey: "presentation", header: "Presentation Status", size: 110, minSize: 90, maxSize: 160, cellData: createDefaultDashCellRenderer("presentation") },
        { columnKey: "actionTracker", header: "Action Tracker Status", size: 120, minSize: 90, maxSize: 180, cellData: createDefaultDashCellRenderer("actionTracker") },
        { columnKey: "outcomesTracker", header: "Outcome Tracker Status", size: 120, minSize: 90, maxSize: 180, cellData: createDefaultDashCellRenderer("outcomesTracker") },
        { columnKey: "licenseToOperate", header: "License To Operate", size: 130, minSize: 100, maxSize: 190, cellData: createLicenseToOperateCellRenderer() },
        { columnKey: "actionItems", header: "Action Items", size: 180, minSize: 120, maxSize: 260, cellData: createLongTextTooltipCellRenderer("actionItems") },
        { columnKey: "recommendations", header: "Recommendation", size: 200, minSize: 140, maxSize: 300, cellData: createLongTextTooltipCellRenderer("recommendations") }
    ];
}

/* istanbul ignore next */
function buildContractBoardsTableColumnMapping(showFolderDetails) {
    return {
        ...(showFolderDetails ? { folderName: "Folder" } : {}),
        date: "Date (YYYY-MM-DD)",
        category: "Category",
        sector: "Sector",
        presenters: "Presenter(s)",
        spend: "Spend ($MN)",
        bidTechnique: "Bid Technique",
        presentation: "Presentation Status",
        actionTracker: "Action Tracker Status",
        outcomesTracker: "Outcome Tracker Status",
        licenseToOperate: "License To Operate",
        actionItems: "Action Items",
        recommendations: "Recommendation"
    };
}

/* istanbul ignore next */
function buildContractBoardsColumnsToInclude(showFolderDetails) {
    return [
        ...(showFolderDetails ? ["Folder"] : []),
        "Date (YYYY-MM-DD)",
        "Category",
        "Sector",
        "Presenter(s)",
        "Spend ($MN)",
        "Bid Technique",
        "Presentation Status",
        "Action Tracker Status",
        "Outcome Tracker Status",
        "License To Operate",
        "Action Items",
        "Recommendation"
    ];
}

let contractBoardsActionItemsTableColumns = [
    { columnKey: "date", header: "Date (YYYY-MM-DD)", size: 90, minSize: 90, maxSize: 140, cellData: createDefaultDashCellRenderer("date") },
    { columnKey: "sector", header: "Sector", size: 110, minSize: 90, maxSize: 160, cellData: createDefaultDashCellRenderer("sector") },
    { columnKey: "category", header: "Category", size: 120, minSize: 90, maxSize: 180, cellData: createDefaultDashCellRenderer("category") },
    { columnKey: "presenter", header: "Presenter(s)", size: 120, minSize: 90, maxSize: 180, cellData: createDefaultDashCellRenderer("presenter") },
    { columnKey: "actionItem", header: "Action Items", size: 180, minSize: 120, maxSize: 260, cellData: createLongTextTooltipCellRenderer("actionItem") },
    { columnKey: "actionItemOwner", header: "Action Item Owner", size: 180, minSize: 120, maxSize: 260, cellData: createLongTextTooltipCellRenderer("actionItemOwner") },
    { columnKey: "status", header: "Action Item Status", size: 180, minSize: 120, maxSize: 260, cellData: createLongTextTooltipCellRenderer("status") },
];

const contractBoardsActionItemsTableColumnMapping = {
    date: "Date (YYYY-MM-DD)",
    sector: "Sector",
    category: "Category",
    presenter: "Presenter(s)",
    actionItem: "Action Items",
    actionItemOwner: "Action Item Owner",
    status: "Action Item Status"
};

const contractBoardsActionItemsColumnsToInclude = [
    "Date (YYYY-MM-DD)",
    "Sector",
    "Category",
    "Presenter(s)",
    "Action Items",
    "Action Item Owner",
    "Action Item Status"
];

const contractBoardsOutcomesSummaryTableColumns = [
    { columnKey: "date", header: "Date (YYYY-MM-DD)", size: 90, minSize: 90, maxSize: 140, cellData: createDefaultDashCellRenderer("date") },
    { columnKey: "sector", header: "Sector", size: 90, minSize: 90, maxSize: 160, cellData: createDefaultDashCellRenderer("sector") },
    { columnKey: "category", header: "Categories", size: 140, minSize: 100, maxSize: 200, cellData: createDefaultDashCellRenderer("category") },
    { columnKey: "presenters", header: "Presenters", size: 140, minSize: 100, maxSize: 200, cellData: createPresenterCellRenderer() },
    { columnKey: "contractTerm", header: "Contract Term", size: 100, minSize: 90, maxSize: 150, cellData: createLegendPillCellRenderer("contractTerm", "contractTermStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "meetCompetition", header: "Meet Competition", size: 130, minSize: 100, maxSize: 200, cellData: createLegendPillCellRenderer("meetCompetition", "meetCompetitionStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "paymentTerms", header: "Payment Terms", size: 150, minSize: 100, maxSize: 200, cellData: createLegendPillCellRenderer("paymentTerms", "paymentTermsStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "requirementAndLiability", header: "Requirements & Liability", size: 160, minSize: 120, maxSize: 220, cellData: createLegendPillCellRenderer("requirementAndLiability", "requirementAndLiabilityStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "priceTransparency", header: "Price Transparency", size: 150, minSize: 110, maxSize: 210, cellData: createLegendPillCellRenderer("priceTransparency", "priceTransparencyStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "productivityValue", header: "Productivity / Value", size: 150, minSize: 110, maxSize: 210, cellData: createLegendPillCellRenderer("productivityValue", "productivityValueStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "terminationClause", header: "Termination Clause", size: 150, minSize: 110, maxSize: 210, cellData: createLegendPillCellRenderer("terminationClause", "terminationClauseStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "freight", header: "Freight", size: 130, minSize: 100, maxSize: 190, cellData: createLegendPillCellRenderer("freight", "freightStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "sustainability", header: "Sustainability", size: 160, minSize: 120, maxSize: 220, cellData: createLegendPillCellRenderer("sustainability", "sustainabilityStatus", contractBoardsOutcomesSummaryLegendItems) },
    { columnKey: "uniqueElements", header: "Unique Elements", size: 160, minSize: 120, maxSize: 220, cellData: createLegendPillCellRenderer("uniqueElements", "uniqueElementsStatus", contractBoardsOutcomesSummaryLegendItems) },
];

const contractBoardsOutcomesSummaryTableColumnMapping = {
    date: "Date",
    sector: "Sector",
    category: "Categories",
    presenters: "Presenters",
    contractTerm: "Contract Term",
    meetCompetition: "Meet Competition",
    paymentTerms: "Payment Terms",
    requirementAndLiability: "Requirements & Liability",
    priceTransparency: "Price Transparency",
    productivityValue: "Productivity / Value",
    terminationClause: "Termination Clause",
    freight: "Freight",
    sustainability: "Sustainability",
    uniqueElements: "Unique Elements",
};

const contractBoardsOutcomesSummaryColumnsToInclude = [
    "Date",
    "Sector",
    "Categories",
    "Presenters",
    "Contract Term",
    "Meet Competition",
    "Payment Terms",
    "Requirements & Liability",
    "Price Transparency",
    "Productivity / Value",
    "Termination Clause",
    "Freight",
    "Sustainability",
    "Unique Elements",
];

function getFilterValue(option) {
    if (typeof option === 'string') {
        return option;
    }
    return option?.displayText || option?.value || '';
}

function mapFilterOptions(values = []) {
    return (values || []).map((option, index) => {
        if (option && typeof option === 'object') {
            const resolvedValue = getFilterValue(option);
            return {
                ...option,
                id: option.id ?? index + 1,
                displayText: option.displayText ?? resolvedValue,
                value: option.value ?? resolvedValue,
            };
        }

        const resolvedValue = String(option ?? '');
        return {
            id: index + 1,
            displayText: resolvedValue,
            value: resolvedValue,
        };
    });
}

function CategoryForums() {
    const dispatch = useDispatch();
    const { authState } = useAuth();
    const [userInfo, setUserInfo] = useState({});
    const [categoryForumConfigData, setCategoryForumConfigData] = useState({});
    const [categoryForumTableData, setCategoryForumTableData] = useState([]);
    const [monthColumnKeys, setMonthColumnKeys] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isUserDataFetched, setIsUserDataFetched] = useState(false);
    const [isConfigLoader, setIsConfigLoader] = useState(false);
    const [isTableLoader, setIsTableLoader] = useState(false);
    const [switcherIndex, setSwitcherIndex] = useState(0);
    const [CategoryForumsSwitcherIndex, setCategoryForumsSwitcherIndex] = useState(0);
    const [updatedCFSwitcherIndex, setUpdatedCFSwitcherIndex] = useState(0);
    const [selectedStep, setSelectedStep] = useState(0);
    const [openKnowMoreModal, setOpenKnowMoreModal] = useState(false)
    const [openContractBoardKnowMoreModal, setOpenContractBoardKnowMoreModal] = useState(false)
    const [openStrategyCouncilKnowMoreModal, setOpenStrategyCouncilKnowMoreModal] = useState(false)
    const [openContractBoardFAQsModal, setOpenContractBoardFAQsModal] = useState(false)
    const [actionTrackerFromDateSelected, setActionTrackerFromDateSelected] = useState([])
    const [actionTrackerToDateSelected, setActionTrackerToDateSelected] = useState([])
    const [actionTrackerSectorSelected, setActionTrackerSectorSelected] = useState([])
    const [actionTrackerCategoryL0Selected, setActionTrackerCategoryL0Selected] = useState([])
    const [actionTrackerCategoryL1Selected, setActionTrackerCategoryL1Selected] = useState([])
    const [actionTrackerOwnerSelected, setActionTrackerOwnerSelected] = useState([])
    const [actionTrackerStatusSelected, setActionTrackerStatusSelected] = useState([])
    const [actionTrackerSelectedYear, setActionTrackerSelectedYear] = useState(new Date().getFullYear());

    const [outcomeSummarySectorList, setOutcomeSummarySectorList] = useState([])
    const [outcomeSummarySectorSelected, setOutcomeSummarySectorSelected] = useState([])
    const [outcomeSummaryCategoryL0List, setOutcomeSummaryCategoryL0List] = useState([])
    const [outcomeSummaryCategoryL0Selected, setOutcomeSummaryCategoryL0Selected] = useState([])
    const [outcomeSummaryCategoryL1List, setOutcomeSummaryCategoryL1List] = useState([])
    const [outcomeSummaryCategoryL1Selected, setOutcomeSummaryCategoryL1Selected] = useState([])
    const [outcomeSummaryPresenterList, setOutcomeSummaryPresenterList] = useState([])
    const [outcomeSummaryPresenterSelected, setOutcomeSummaryPresenterSelected] = useState([])

    const [outcomeSummaryFromDates, setOutcomeSummaryFromDates] = useState([]);
    const [outcomeSummaryToDates, setOutcomeSummaryToDates] = useState([]);
    const [outcomeSummaryFromDateSelected, setOutcomeSummaryFromDateSelected] = useState([])
    const [outcomeSummaryToDateSelected, setOutcomeSummaryToDateSelected] = useState([])
    const [outcomeSummaryEntireMonthList, setOutcomeSummaryEntireMonthList] = useState([])
    const [isOutcomeSummaryInitialized, setIsOutcomeSummaryInitialized] = useState(false)




    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [contractBoardSelectedYear, setContractBoardSelectedYear] = useState(new Date().getFullYear());

    const [tableLoaded, setTableLoaded] = useState(false);
    const [legendItems, setLegendItems] = useState([]);
    const cfContractBoardTableRef = useRef(null);

    const categoryForumsContractBoardRef = useRef();
    const forumsByMonthRef = useRef();
    const completedVsRemainingRef = useRef();

    const contractBoardsActionTrackerRef = useRef();
    const contractBoardsOutcomesSummaryRef = useRef();
    const contractBoardsActionItemsTableRef = useRef();
    const actionTrackerForumsByMonthRef = useRef();
    const actionTrackerStatusRef = useRef();
    const isSectorFilterInitialized = useRef(false);
    const isCategoryL0FilterInitialized = useRef(false);
    const isCategoryL1FilterInitialized = useRef(false);
    const isOwnerFilterInitialized = useRef(false);
    const isStatusFilterInitialized = useRef(false);

    const feedbackFormUrl = useSelector((state) => state?.localData?.data[localStoreKeys.feedbackFormUrl]);

    const [activeId, setActiveId] = useState(null);

    const userData = useSelector((state) => state.getLandingPageReducer);
    const categoryForumConfig = useSelector((state) => state.getCategoryForumConfigReducer);
    const categoryForumTable = useSelector((state) => state.getCategoryForumTableReducer);

    const categoryForumYearToDate = useSelector((state) => state.getCategoryForumYearToDateReducer);
    const categoryForumForumsByCategory = useSelector((state) => state.getCategoryForumForumsByCategoryReducer);
    const categoryForumBalanceOfTheYear = useSelector((state) => state.getCategoryForumBalanceOfTheYearReducer);
    const categoryForumSummary = useSelector((state) => state.getCategoryForumSummaryReducer);
    const categoryForumContractBoardsDetails = useSelector((state) => state.getCategoryForumContractBoardsDetailsReducer);
    const categoryForumActionTrackerFilters = useSelector((state) => state.getCategoryForumActionTrackerFiltersReducer);
    const categoryForumActionTrackerHeaderData = useSelector((state) => state.getCategoryForumActionTrackerHeaderDataReducer);
    const categoryForumActionTrackerTabularData = useSelector((state) => state.getCategoryForumActionTrackerTabularDataReducer);
    const categoryForumActionTrackerChartData = useSelector((state) => state.getCategoryForumActionTrackerChartDataReducer);

    const categoryForumOutcomeSummaryFiltersData = useSelector((state) => state.getCategoryForumOutcomeSummaryFiltersReducer);
    const categoryForumOutcomeSummaryTabularData = useSelector((state) => state.getCategoryForumOutcomeSummaryTabularDataReducer);

    const isCategoryForumYearToDateLoading = !!categoryForumYearToDate?.loading;
    const isCategoryForumForumsByCategoryLoading = !!categoryForumForumsByCategory?.loading;
    const isCategoryForumBalanceOfTheYearLoading = !!categoryForumBalanceOfTheYear?.loading;
    const isCategoryForumSummaryLoading = !!categoryForumSummary?.loading;
    const isCategoryForumContractBoardsDetailsLoading = !!categoryForumContractBoardsDetails?.loading;

    const isActionTrackerKpiLoading = !!categoryForumActionTrackerHeaderData?.loading;
    const isActionTrackerTableLoading = !!categoryForumActionTrackerTabularData?.loading;
    const isActionTrackerChartsLoading = !!categoryForumActionTrackerChartData?.loading;

    const isOutcomeSummaryTableLoading = !!categoryForumOutcomeSummaryTabularData?.loading;

    const actionTrackerFiltersLoaded = categoryForumActionTrackerFilters?.type === 'SUCCESS_CATEGORY_FORUM_ACTION_TRACKER_FILTERS_ACTION';
    // Use reducer state directly — API responses spread at top level, not nested under .data
    const actionTrackerMainFiltersData = actionTrackerFiltersLoaded ? categoryForumActionTrackerFilters : null;
    // Reject stale responses: only use data whose requestedYear matches the currently selected year
    const actionTrackerKPIData = categoryForumActionTrackerHeaderData?.type === 'SUCCESS_CATEGORY_FORUM_ACTION_TRACKER_HEADER_DATA_ACTION'
        && categoryForumActionTrackerHeaderData?.requestedYear === actionTrackerSelectedYear
        ? categoryForumActionTrackerHeaderData
        : null;
    const actionTrackerTableData = categoryForumActionTrackerTabularData?.requestedYear === actionTrackerSelectedYear
        && Array.isArray(categoryForumActionTrackerTabularData?.data) ? categoryForumActionTrackerTabularData.data : [];
    const actionTrackerStatusLegends = categoryForumActionTrackerTabularData?.requestedYear === actionTrackerSelectedYear
        && Array.isArray(categoryForumActionTrackerTabularData?.legends)
        ? categoryForumActionTrackerTabularData.legends.map((legend) => ({
            label: legend?.categoryName,
            color: legend?.colorCode,
            strokeColor: legend?.borderColor,
        }))
        : [];
    const actionTrackerChartData = categoryForumActionTrackerChartData?.type === 'SUCCESS_CATEGORY_FORUM_ACTION_TRACKER_CHART_DATA_ACTION'
        && categoryForumActionTrackerChartData?.requestedYear === actionTrackerSelectedYear
        ? categoryForumActionTrackerChartData
        : null;

    const contractBoardForumsCompleted = categoryForumYearToDate?.yearToDate?.[String(contractBoardSelectedYear)]?.contractBoardForumsCompleted ?? 0;
    const totalSpendYearToDate = splitValueWithUnit(categoryForumYearToDate?.yearToDate?.[String(contractBoardSelectedYear)]?.totalSpend ?? "0");

    const categoriesCompletedYearToDate = categoryForumYearToDate?.yearToDate?.[String(contractBoardSelectedYear)]?.categoriesCompleted ?? [];
    const totalSubcategoriesCompletedYearToDate = (categoriesCompletedYearToDate || []).reduce((sum, cat) => sum + (Array.isArray(cat?.subcategories) ? cat.subcategories.length : 0), 0);
    const lastUpdateContractBoard = categoryForumYearToDate?.lastUpdate;
    const refreshFrequencyContractBoard = categoryForumYearToDate?.refreshFrequency;
    // const subCategoriesCompletedYearToDate = categoriesCompletedYearToDate.flatMap(category => category.subcategories);
    const yearToDateCategoryGroups = buildCategoryGroupsFromCategoryObjects(categoriesCompletedYearToDate);

    const contractBoardForumsScheduled = categoryForumBalanceOfTheYear?.balanceOfTheYear?.[String(contractBoardSelectedYear)]?.contractBoardForumsScheduled ?? 0;

    const estimatedSpendBalanceOfYear = splitValueWithUnit(categoryForumBalanceOfTheYear?.balanceOfTheYear?.[String(contractBoardSelectedYear)]?.estimatedSpend ?? "0");
    const categoriesScheduledBalanceOfYear = categoryForumBalanceOfTheYear?.balanceOfTheYear?.[String(contractBoardSelectedYear)]?.categoriesScheduled ?? [];
    const totalSubcategoriesScheduledBalanceOfYear = (categoriesScheduledBalanceOfYear || []).reduce((sum, cat) => sum + (Array.isArray(cat?.subcategories) ? cat.subcategories.length : 0), 0);
    // const subCategoriesScheduledBalanceOfYear = categoriesScheduledBalanceOfYear.flatMap(category => category.subcategories);
    const balanceOfYearCategoryGroups = buildCategoryGroupsFromCategoryObjects(categoriesScheduledBalanceOfYear);

    const ContractBoardDonutChartData = categoryForumSummary?.summary?.[String(contractBoardSelectedYear)] ?? [];
    const totalActionItemsCount = actionTrackerKPIData?.actionItemCount;
    const completedActionItemsCount = actionTrackerKPIData?.completedActionItemCount;
    const actionTrackerContractForumsCompletedCount = actionTrackerKPIData?.contractForumsCompletedCount;
    const actionTrackerLastUpdate = actionTrackerKPIData?.lastUpdate;
    const actionTrackerRefreshFrequency = actionTrackerKPIData?.refreshFrequency;

    const forumsByMonthBarChartData = categoryForumForumsByCategory?.forumsByCategory?.[String(contractBoardSelectedYear)]?.barChartData ?? [];
    const forumsByMonthBarChartLegends = categoryForumForumsByCategory?.forumsByCategory?.[String(contractBoardSelectedYear)]?.barChartLegends ?? [];
    const processedforumsByMonthBarChartLegends = (forumsByMonthBarChartLegends && forumsByMonthBarChartLegends.length > 0)
        ? forumsByMonthBarChartLegends.map((legend) => ({
            strokeColor: legend.borderColor,
            color: legend.colorCode,
            label: legend.categoryName,
        }))
        : [];

    const contractBoardsDetailsData = categoryForumContractBoardsDetails?.contractBoardsDetails?.[String(contractBoardSelectedYear)] ?? [];

    const effectiveContractBoardsTableData = contractBoardsDetailsData?.length > 0 ? contractBoardsDetailsData : [];

    const showContractBoardsFolderColumn = !!categoryForumContractBoardsDetails?.showFolderDetails;
    const contractBoardsTableColumns = useMemo(() => buildContractBoardsTableColumns(showContractBoardsFolderColumn), [showContractBoardsFolderColumn]);
    const contractBoardsTableColumnMapping = useMemo(() => buildContractBoardsTableColumnMapping(showContractBoardsFolderColumn), [showContractBoardsFolderColumn]);
    const contractBoardsColumnsToInclude = useMemo(() => buildContractBoardsColumnsToInclude(showContractBoardsFolderColumn), [showContractBoardsFolderColumn]);


    const effectiveContractBoardsActionItemsTableData = Array.isArray(actionTrackerTableData) ? actionTrackerTableData : [];
    const actionTrackerCategoryDataFromApi = Array.isArray(actionTrackerMainFiltersData?.category) ? actionTrackerMainFiltersData.category : [];

    const actionTrackerYear = Number(actionTrackerSelectedYear);
    const actionTrackerAllDateOptions = Number.isNaN(actionTrackerYear)
        ? []
        : getToDateOptionsWithMaxDate(`Jan ${actionTrackerYear}`, {
            maxDate: `${actionTrackerYear}-12-01`,
        });

    const selectedActionTrackerFromDateValue = actionTrackerFromDateSelected?.[0]?.value;
    const selectedActionTrackerToDateValue = actionTrackerToDateSelected?.[0]?.value;
    const selectedActionTrackerFromDateLabel = actionTrackerFromDateSelected?.[0]?.displayText;
    const selectedActionTrackerFromMonthKey = String(selectedActionTrackerFromDateValue || '').slice(0, 7);
    const selectedActionTrackerToMonthKey = String(selectedActionTrackerToDateValue || '').slice(0, 7);

    const actionTrackerFromDateOptions = !selectedActionTrackerToMonthKey
        ? actionTrackerAllDateOptions
        : getToDateOptionsWithMaxDate(`Jan ${actionTrackerYear}`, {
            maxDate: selectedActionTrackerToDateValue,
        });

    const actionTrackerToDateOptions = !selectedActionTrackerFromMonthKey
        ? actionTrackerAllDateOptions
        : getToDateOptionsWithMaxDate(selectedActionTrackerFromDateLabel, {
            maxDate: `${actionTrackerYear}-12-01`,
        });

    const actionTrackerSectorOptions = mapFilterOptions(actionTrackerMainFiltersData?.sectors || []);
    const actionTrackerCategoryL0Options = mapFilterOptions(actionTrackerCategoryDataFromApi.map((categoryItem) => categoryItem?.name).filter(Boolean));
    const selectedL0Values = (actionTrackerCategoryL0Selected || []).map(getFilterValue).filter(Boolean);
    const actionTrackerCategoryL1Options = mapFilterOptions(actionTrackerCategoryDataFromApi
        .filter((categoryItem) => selectedL0Values.includes(categoryItem?.name))
        .flatMap((categoryItem) => (Array.isArray(categoryItem?.categoryL1) ? categoryItem.categoryL1 : []))
        .filter(Boolean));
    const actionTrackerOwnerOptions = mapFilterOptions(actionTrackerMainFiltersData?.owners || []);
    const actionTrackerStatusOptions = mapFilterOptions(actionTrackerMainFiltersData?.status || []);

    const ActionTrackerDonutChartData = actionTrackerChartData?.summary || [];
    const actionTrackerBarChartData = actionTrackerChartData?.barChartData || [];
    const actionTrackerBarChartLegends = actionTrackerChartData?.barChartLegends || [];
    const processedActionTrackerBarChartLegends = (actionTrackerBarChartLegends && actionTrackerBarChartLegends.length > 0)
        ? actionTrackerBarChartLegends.map((legend) => ({
            color: legend.colorCode,
            strokeColor: legend.borderColor,
            label: legend.categoryName,
        }))
        : [];

    const contractBoardsActionItemsColumns = contractBoardsActionItemsTableColumns.map((column) =>
        column.columnKey === 'status'
            ? { ...column, cellData: createStatusLegendCellRenderer(actionTrackerStatusLegends) }
            : column
    );

    const areActionTrackerTopFiltersInitialized = () => (
        isSectorFilterInitialized.current
        && isCategoryL0FilterInitialized.current
        && isCategoryL1FilterInitialized.current
        && isOwnerFilterInitialized.current
        && isStatusFilterInitialized.current
    );

    const buildActionTrackerPayload = ({ includeDateRange = false } = {}) => {
        const selectedYear = Number(actionTrackerSelectedYear);
        const payload = {
            sector: (actionTrackerSectorSelected || []).map(getFilterValue).filter(Boolean),
            owner: (actionTrackerOwnerSelected || []).map(getFilterValue).filter(Boolean),
            status: (actionTrackerStatusSelected || []).map(getFilterValue).filter(Boolean),
            categoryL0: (actionTrackerCategoryL0Selected || []).map(getFilterValue).filter(Boolean),
            categoryL1: (actionTrackerCategoryL1Selected || []).map(getFilterValue).filter(Boolean),
            year: selectedYear,
        };

        if (includeDateRange) {
            const defaultFromDate = `${selectedYear}-01-01`;
            const defaultToDate = `${selectedYear}-12-01`;
            
            // Ensure dates match the current year; if stale, use defaults
            const fromDateYear = selectedActionTrackerFromDateValue 
                ? parseInt(selectedActionTrackerFromDateValue.split('-')[0], 10)
                : null;
            const toDateYear = selectedActionTrackerToDateValue
                ? parseInt(selectedActionTrackerToDateValue.split('-')[0], 10)
                : null;
            
            payload.fromDate = (fromDateYear === selectedYear && selectedActionTrackerFromDateValue) 
                ? selectedActionTrackerFromDateValue 
                : defaultFromDate;
            payload.toDate = (toDateYear === selectedYear && selectedActionTrackerToDateValue)
                ? selectedActionTrackerToDateValue
                : defaultToDate;
        }

        return payload;
    };

    const actionTrackerDisplayTableData = effectiveContractBoardsActionItemsTableData;

    const onActionTrackerDateFiltersReset = () => {
        setActionTrackerFromDateSelected(actionTrackerAllDateOptions.length > 0 ? [actionTrackerAllDateOptions[0]] : []);
        setActionTrackerToDateSelected(actionTrackerAllDateOptions.length > 0 ? [actionTrackerAllDateOptions[actionTrackerAllDateOptions.length - 1]] : []);
    };

    const { showToast } = useToast();

    // Switcher tab indices shift depending on whether the "Your Category Forums" tab is shown
    const showYourCategoryForums = !!categoryForumConfigData?.showYourCategoryForums;
    const overviewTabIndex = 0;
    const yourCategoryForumsTabIndex = showYourCategoryForums ? 1 : -1;
    const dashboardTabIndex = showYourCategoryForums ? 2 : 1;
    const actionTrackerTabIndex = showYourCategoryForums ? 3 : 2;
    const outcomesSummaryTabIndex = showYourCategoryForums ? 4 : 3;

    /* istanbul ignore next */
    const downloadExcel = async () => {
        const isActionItemsTracker = CategoryForumsSwitcherIndex === actionTrackerTabIndex;
        const isOutcomesSummary = CategoryForumsSwitcherIndex === outcomesSummaryTabIndex;

        const columnsToInclude = isActionItemsTracker
            ? ["DATE", "SECTOR", "CATEGORY", "PRESENTERS", "ACTION ITEMS", "ACTION ITEM OWNER", "ACTION ITEM STATUS"] :
            isOutcomesSummary ? contractBoardsOutcomesSummaryColumnsToInclude 
            : ["DATE", "CATEGORY SUBGROUP (PRIMARY)", "SECTOR", "CATEGORY PRESENTER(S)"];

        const transformedData = isActionItemsTracker
            ? (actionTrackerDisplayTableData || []).map((item = {}) => ({
                "DATE": item.date,
                "SECTOR": item.sector,
                "CATEGORY": item.category,
                "PRESENTERS": item.presenter,
                "ACTION ITEMS": item.actionItem,
                "ACTION ITEM OWNER": item.actionItemOwner,
                "ACTION ITEM STATUS": item.status,
            }))
            : isOutcomesSummary
            ? (categoryForumOutcomeSummaryTabularData?.tabularDetails || []).map((item = {}) => ({
                "Date": item.date,
                "Sector": item.sector,
                "Categories": item.category,
                "Presenters": item.presenters,
                "Contract Term": item.contractTerm,
                "Meet Competition": item.meetCompetition,
                "Payment Terms": item.paymentTerms,
                "Requirements & Liability": item.requirementAndLiability,
                "Price Transparency": item.priceTransparency,
                "Productivity / Value": item.productivityValue,
                "Termination Clause": item.terminationClause,
                "Freight": item.freight,
                "Sustainability": item.sustainability,
                "Unique Elements": item.uniqueElements,
            }))
            : (categoryForumTable?.excelTableData || []).map((item = {}) => ({
                "DATE": item.date,
                "CATEGORY SUBGROUP (PRIMARY)": item.categorySubGroupPrimary,
                "SECTOR": item.sector,
                "CATEGORY PRESENTER(S)": item.categoryPresentors,
            }));

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const formattedDate = `${yyyy}${mm}${dd}`;

        await exportRowsToExcel({
            rows: transformedData,
            headers: columnsToInclude,
            fileName: isActionItemsTracker
                ? `Contract Boards Action Items_${formattedDate}.xlsx`
                : isOutcomesSummary
                ? `Contract Boards Outcomes Summary_${formattedDate}.xlsx`
                : `Annual Category Forums Calendar_${formattedDate}.xlsx`,
        });
    };

    /* istanbul ignore next */
    const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
        if (isPdfDownloaded) {
            if (isModal) {
                showToast('success', !isJpeg ? "PDF of full page successfully downloaded." : 'JPEG of full page successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            } else if (isTable) {
                showToast('success', !isJpeg ? "PDF of a table successfully downloaded." : 'JPEG of a table successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
            else {
                showToast('success', !isJpeg ? "PDF of a chart successfully downloaded." : 'JPEG of a chart successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
        }
        else if (isExcel) {
            downloadExcel()
            // showToast('success', 'XLSX of a table successfully downloaded.', {
            //         icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
            // });
        }
        else {
            showToast('Error', "Failed to download. Please try again.", {
                icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
            });
        }
    }
    const staticOwnersData = [
        {
            "name": "",
            "emailId": "globalprocurementexcellence@pepsico.com"
        },
        {
            "name": "",
            "emailId": "rachel.thomas@pepsico.com"
        },
        {
            "name": "",
            "emailId": "rajeet.sengupta@pepsico.com"
        },

    ]
    useLoginEffects(userData);

    useEffect(() => {
        async function fetchUserInfo() {
            const user = await getUserClaims();
            if (user) {
                localStorage.setItem('userInfoEmailID', btoa(JSON.stringify(user?.useremail)));
                setUserInfo(user);
                dispatch(FetchLandingPageAction(user));
                setIsUserDataFetched(true);
                setIsConfigLoader(true);
                setIsTableLoader(true);
            }
        }
        setIsDataLoading(true);
        fetchUserInfo();
    }, []);

    useEffect(() => {
        if (isUserDataFetched && userData && userData?.type === "SUCCESS_LANDING_PAGE_ACTION") {
            // Extract and store feedbackFormUrl from userData
            if (userData?.feedbackFormUrl) {
                dispatch(setKeyValue({ key: localStoreKeys.feedbackFormUrl, value: userData.feedbackFormUrl }));
            }
            call7DaysNpsLogic(dispatch, userData);
            setIsDataLoading(false);
            setIsUserDataFetched(false);
            dispatch(FetchCategoryForumConfigAction(userData?.emailId));
            dispatch(FetchCategoryForumTableAction(selectedYear));
            dispatch(FetchCategoryForumYearToDateDateAction());
            dispatch(FetchCategoryForumForumsByCategoryAction());
            dispatch(FetchCategoryForumBalanceOfTheYearAction());
            dispatch(FetchCategoryForumSummaryAction());
            dispatch(FetchCategoryForumContractBoardsDetailsAction(userData?.emailId));
            dispatch(FetchCategoryForumOutcomeSummaryFiltersAction());
        }
    }, [userData, isUserDataFetched]);

    useEffect(() => {
        if (isConfigLoader && categoryForumConfig && categoryForumConfig.type === "SUCCESS_CATEGORY_FORUM_CONFIG_ACTION") {
            setIsConfigLoader(false);
            setCategoryForumConfigData(categoryForumConfig)
        }
    }, [categoryForumConfig, isConfigLoader])

    useEffect(() => {
        if (categoryForumOutcomeSummaryFiltersData && categoryForumOutcomeSummaryFiltersData.type === "SUCCESS_CATEGORY_FORUM_OUTCOME_SUMMARY_FILTERS_ACTION") {
            const data = categoryForumOutcomeSummaryFiltersData?.sectors;

            const allSectors = getOutcomeSummaryDropdownOptions([...new Set((data || []).map(s => s.name))]);
            setOutcomeSummarySectorList(allSectors);
            setOutcomeSummarySectorSelected(allSectors);
            localStorage.setItem("outcomeSummary-sector", JSON.stringify(allSectors));

            const allCategoryL0 = mergeUniqueOptions(
                allSectors.map((sector) => getCategoryL0BySector(data, sector?.displayText))
            );
            setOutcomeSummaryCategoryL0List(allCategoryL0);
            setOutcomeSummaryCategoryL0Selected(allCategoryL0);
            localStorage.setItem("outcomeSummary-categoryL0", JSON.stringify(allCategoryL0));

            const allCategoryL1 = mergeUniqueOptions(
                allSectors.flatMap((sector) => allCategoryL0.map((l0) => getCategoryL1BySectorAndL0(data, sector?.displayText, l0?.displayText)))
            );
            setOutcomeSummaryCategoryL1List(allCategoryL1);
            setOutcomeSummaryCategoryL1Selected(allCategoryL1);
            localStorage.setItem("outcomeSummary-categoryL1", JSON.stringify(allCategoryL1));

            const allPresenter = mergeUniqueOptions(
                allSectors.flatMap((sector) => allCategoryL0.flatMap((l0) => allCategoryL1.map((l1) => getPresenterBySectorL0AndL1(data, sector?.displayText, l0?.displayText, l1?.displayText))))
            );
            setOutcomeSummaryPresenterList(allPresenter);
            setOutcomeSummaryPresenterSelected(allPresenter);
            localStorage.setItem("outcomeSummary-presenter", JSON.stringify(allPresenter));
            const outcomeSummaryDateRange = initializeOutcomeSummaryDateRanges();
            loadOutcomeSummaryData({
                sector: allSectors,
                categoryL0: allCategoryL0,
                categoryL1: allCategoryL1,
                presenter: allPresenter,
                fromDate: outcomeSummaryDateRange?.defaultFrom?.value || null,
                toDate: outcomeSummaryDateRange?.defaultTo?.value || null,
            });
            setIsOutcomeSummaryInitialized(true);
        }
    }, [categoryForumOutcomeSummaryFiltersData])

    // Whenever any Outcome Summary filter selection actually changes (after the initial load),
    // fetch tabular data using the latest state directly. This avoids relying on values read
    // inside onChange handlers, which can be invoked with a stale closure by the dropdown component.
    useEffect(() => {
        if (!isOutcomeSummaryInitialized) return;
        loadOutcomeSummaryData();
    }, [
        outcomeSummarySectorSelected,
        outcomeSummaryCategoryL0Selected,
        outcomeSummaryCategoryL1Selected,
        outcomeSummaryPresenterSelected,
        outcomeSummaryFromDateSelected,
        outcomeSummaryToDateSelected
    ])

    useEffect(() => {
        if (categoryForumTable && categoryForumTable.type === "SUCCESS_CATEGORY_FORUM_TABLE_ACTION") {
            const apiData = categoryForumTable?.categoryForumsTabledata ?? [];
            const apiLegends = categoryForumTable?.legends;
            const processedLegends = (apiLegends && apiLegends.length > 0)
                ? apiLegends.map((legend) => ({
                    label: legend.categoryName,
                    color: legend.colorCode,
                    strokeColor: legend.borderColor,
                }))
                : [];
            setIsTableLoader(false);
            setTableLoaded(true);
            const convertedTableData = convertForumTableData(apiData);
            const { monthKeys, columns } = generateColumnsFromApiData(apiData, processedLegends);
            setCategoryForumTableData(convertedTableData);
            setMonthColumnKeys(monthKeys);
            setTableColumns(columns);
            setLegendItems(processedLegends);
        }
    }, [categoryForumTable])

    // Re-fetch table data when selected year changes (after initial load)
    useEffect(() => {
        if (tableLoaded) {
            dispatch({ type: 'RESET_CATEGORY_FORUM_TABLE_ACTION', payload: 'LOADING_CATEGORY_FORUM_TABLE_ACTION' });
            setIsTableLoader(true);
            dispatch(FetchCategoryForumTableAction(selectedYear));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear])

    useEffect(() => {
        if (actionTrackerFiltersLoaded) {
            return;
        }
        dispatch(FetchCategoryForumActionTrackerFiltersAction());
    }, [actionTrackerFiltersLoaded]);

    // Memoize the stringified payload so effect only runs when payload actually changes
    const memoizedTabularPayload = useMemo(() => {
        if (!actionTrackerMainFiltersData || !areActionTrackerTopFiltersInitialized()) {
            return null;
        }
        return JSON.stringify(buildActionTrackerPayload({ includeDateRange: true }));
    }, [
        actionTrackerSectorSelected,
        actionTrackerOwnerSelected,
        actionTrackerStatusSelected,
        actionTrackerCategoryL0Selected,
        actionTrackerCategoryL1Selected,
        actionTrackerSelectedYear,
        selectedActionTrackerFromDateValue,
        selectedActionTrackerToDateValue,
        actionTrackerMainFiltersData,
    ]);

    // Fetch tabular data only when payload changes
    useEffect(() => {
        if (memoizedTabularPayload === null) {
            return;
        }
        dispatch(FetchCategoryForumActionTrackerTabularDataAction(JSON.parse(memoizedTabularPayload)));
    }, [memoizedTabularPayload, dispatch]);

    useEffect(() => {
        if (!actionTrackerMainFiltersData || !areActionTrackerTopFiltersInitialized()) {
            return;
        }
        dispatch(FetchCategoryForumActionTrackerHeaderDataAction(buildActionTrackerPayload()));
    }, [
        actionTrackerSectorSelected,
        actionTrackerOwnerSelected,
        actionTrackerStatusSelected,
        actionTrackerCategoryL0Selected,
        actionTrackerCategoryL1Selected,
        actionTrackerSelectedYear,
        actionTrackerMainFiltersData,
    ]);

    useEffect(() => {
        if (!actionTrackerMainFiltersData || !areActionTrackerTopFiltersInitialized()) {
            return;
        }
        dispatch(FetchCategoryForumActionTrackerChartDataAction(buildActionTrackerPayload()));
    }, [
        actionTrackerSectorSelected,
        actionTrackerOwnerSelected,
        actionTrackerStatusSelected,
        actionTrackerCategoryL0Selected,
        actionTrackerCategoryL1Selected,
        actionTrackerSelectedYear,
        actionTrackerMainFiltersData,
    ]);

    useEffect(() => {
        if (actionTrackerAllDateOptions.length === 0) {
            setActionTrackerFromDateSelected([]);
            setActionTrackerToDateSelected([]);
            return;
        }

        const fromMonthDefaultOption = actionTrackerAllDateOptions[0];
        const toMonthDefaultOption = actionTrackerAllDateOptions[actionTrackerAllDateOptions.length - 1];

        const fromSelectionStillValid = actionTrackerFromDateSelected?.[0]?.displayText
            && actionTrackerFromDateOptions.some((option) => option.displayText === actionTrackerFromDateSelected[0].displayText);
        const toSelectionStillValid = actionTrackerToDateSelected?.[0]?.displayText
            && actionTrackerToDateOptions.some((option) => option.displayText === actionTrackerToDateSelected[0].displayText);

        // Default to Jan when no valid From Month selection exists
        if (!fromSelectionStillValid) {
            setActionTrackerFromDateSelected(fromMonthDefaultOption ? [fromMonthDefaultOption] : []);
        }

        // Default to Dec when no valid To Month selection exists
        if (!toSelectionStillValid) {
            setActionTrackerToDateSelected(toMonthDefaultOption ? [toMonthDefaultOption] : []);
        }
    }, [actionTrackerSelectedYear, actionTrackerAllDateOptions, actionTrackerFromDateOptions, actionTrackerToDateOptions]);

    /* istanbul ignore next */
    const onActionTrackerSectorChange = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one sector is required' });
        }
        setActionTrackerSectorSelected(val || []);
    };

    /* istanbul ignore next */
    const onActionTrackerOwnerChange = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one action items owner is required' });
        }
        setActionTrackerOwnerSelected(val || []);
    };

    /* istanbul ignore next */
    const onActionTrackerStatusChange = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one action items status is required' });
        }
        setActionTrackerStatusSelected(val || []);
    };

    /* istanbul ignore next */
    const onActionTrackerCategoryL1Change = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one category L1 is required' });
        }
        setActionTrackerCategoryL1Selected(val || []);
    };

    /* istanbul ignore next */
    const onActionTrackerFromDateChange = (val) => {
        setActionTrackerFromDateSelected(val);
    };

    /* istanbul ignore next */
    const onActionTrackerToDateChange = (val) => {
        setActionTrackerToDateSelected(val);
    };

    /* istanbul ignore next */
    const onActionTrackerCategoryL0Change = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one category L0 is required' });
        }
        setActionTrackerCategoryL0Selected(val || []);
        // Auto-select all L1s that belong to the newly selected L0s (mirrors KPIOverview behavior)
        const newL0Values = (val || []).map(getFilterValue).filter(Boolean);
        const allL1ForNewL0 = mapFilterOptions(actionTrackerCategoryDataFromApi
            .filter((categoryItem) => newL0Values.includes(categoryItem?.name))
            .flatMap((categoryItem) => (Array.isArray(categoryItem?.categoryL1) ? categoryItem.categoryL1 : []))
            .filter(Boolean));
        setActionTrackerCategoryL1Selected(allL1ForNewL0);
    };

    /* istanbul ignore next */
    const onOutcomeSummarySectorChange = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one sector is required' });
        }
        const selectedSectors = val || [];
        setOutcomeSummarySectorSelected(selectedSectors);

        const data = categoryForumOutcomeSummaryFiltersData?.sectors;
        const allCategoryL0 = mergeUniqueOptions(
            selectedSectors.map((sector) => getCategoryL0BySector(data, sector?.displayText))
        );
        setOutcomeSummaryCategoryL0List(allCategoryL0);
        setOutcomeSummaryCategoryL0Selected(allCategoryL0);

        const allCategoryL1 = mergeUniqueOptions(
            selectedSectors.flatMap((sector) => allCategoryL0.map((l0) => getCategoryL1BySectorAndL0(data, sector?.displayText, l0?.displayText)))
        );
        setOutcomeSummaryCategoryL1List(allCategoryL1);
        setOutcomeSummaryCategoryL1Selected(allCategoryL1);

        const allPresenters = mergeUniqueOptions(
            selectedSectors.flatMap((sector) => allCategoryL0.flatMap((l0) => allCategoryL1.map((l1) => getPresenterBySectorL0AndL1(data, sector?.displayText, l0?.displayText, l1?.displayText))))
        );
        setOutcomeSummaryPresenterList(allPresenters);
        setOutcomeSummaryPresenterSelected(allPresenters);
    };

    /* istanbul ignore next */
    const onOutcomeSummaryCategoryL0Change = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one category L0 is required' });
        }
        const selectedCategoryL0 = val || [];
        setOutcomeSummaryCategoryL0Selected(selectedCategoryL0);

        const data = categoryForumOutcomeSummaryFiltersData?.sectors;
        const selectedSectors = outcomeSummarySectorSelected || [];

        const allCategoryL1 = mergeUniqueOptions(
            selectedSectors.flatMap((sector) => selectedCategoryL0.map((l0) => getCategoryL1BySectorAndL0(data, sector?.displayText, l0?.displayText)))
        );
        setOutcomeSummaryCategoryL1List(allCategoryL1);
        setOutcomeSummaryCategoryL1Selected(allCategoryL1);

        const allPresenters = mergeUniqueOptions(
            selectedSectors.flatMap((sector) => selectedCategoryL0.flatMap((l0) => allCategoryL1.map((l1) => getPresenterBySectorL0AndL1(data, sector?.displayText, l0?.displayText, l1?.displayText))))
        );
        setOutcomeSummaryPresenterList(allPresenters);
        setOutcomeSummaryPresenterSelected(allPresenters);
    };

    /* istanbul ignore next */
    const onOutcomeSummaryCategoryL1Change = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one category L1 is required' });
        }
        const selectedCategoryL1 = val || [];
        setOutcomeSummaryCategoryL1Selected(selectedCategoryL1);

        const data = categoryForumOutcomeSummaryFiltersData?.sectors;
        const selectedSectors = outcomeSummarySectorSelected || [];
        const selectedCategoryL0 = outcomeSummaryCategoryL0Selected || [];

        const allPresenters = mergeUniqueOptions(
            selectedSectors.flatMap((sector) => selectedCategoryL0.flatMap((l0) => selectedCategoryL1.map((l1) => getPresenterBySectorL0AndL1(data, sector?.displayText, l0?.displayText, l1?.displayText))))
        );
        setOutcomeSummaryPresenterList(allPresenters);
        setOutcomeSummaryPresenterSelected(allPresenters);
    };

    /* istanbul ignore next */
    const onOutcomeSummaryPresenterChange = (val) => {
        if (Array.isArray(val) && val.length === 0) {
            message.warning({ content: 'At least one presenter is required' });
        }
        setOutcomeSummaryPresenterSelected(val || []);
    };

    /* istanbul ignore next */
    const changeFromDateOptions = toDateMonthSelected => {
        const filteredMonths = changeFromDateOptionsUtil(
        toDateMonthSelected,
        outcomeSummaryEntireMonthList
        );
        setOutcomeSummaryFromDates(filteredMonths);
    };

    /* istanbul ignore next */
    const changeToDateOptions = fromDateMonthSelected => {
        const filteredMonths = changeToDateOptionsUtil(
        fromDateMonthSelected,
        outcomeSummaryEntireMonthList
        );
        setOutcomeSummaryToDates(filteredMonths);
    };

    /* istanbul ignore next */
    const onOutcomeSummaryFromDateChange = val => {
        if (val && val.length > 0) {
            setOutcomeSummaryFromDateSelected(val);
            localStorage.setItem("outcomeSummary-fromDateSelected", JSON.stringify(val));

            changeToDateOptions(val[0]);
        } 
    };

    /* istanbul ignore next */
    const onOutcomeSummaryToDateChange = val => {

        if (val && val.length > 0) {
        setOutcomeSummaryToDateSelected(val);
        localStorage.setItem("outcomeSummary-toDateSelected", JSON.stringify(val));
        changeFromDateOptions(val[0]); // Assuming this function is correctly defined elsewhere
        } 
    };

    /* istanbul ignore next */
    const onActionTrackerFiltersReset = () => {
        setActionTrackerSectorSelected(actionTrackerSectorOptions);
        setActionTrackerCategoryL0Selected(actionTrackerCategoryL0Options);
        // Derive all L1s from all L0 categories, not from current (partial) L0 selection
        const allL1Options = mapFilterOptions(actionTrackerCategoryDataFromApi
            .flatMap((categoryItem) => (Array.isArray(categoryItem?.categoryL1) ? categoryItem.categoryL1 : []))
            .filter(Boolean));
        setActionTrackerCategoryL1Selected(allL1Options);
        setActionTrackerOwnerSelected(actionTrackerOwnerOptions);
        setActionTrackerStatusSelected(actionTrackerStatusOptions);
    };

    /* istanbul ignore next */
    const onOutcomeSummaryFiltersReset = () => {
        dispatch(FetchCategoryForumOutcomeSummaryFiltersAction());
    };

    useEffect(() => {
        if (!isSectorFilterInitialized.current && actionTrackerSectorOptions.length > 0) {
            setActionTrackerSectorSelected(actionTrackerSectorOptions);
            isSectorFilterInitialized.current = true;
        }

        if (!isCategoryL0FilterInitialized.current && actionTrackerCategoryL0Options.length > 0) {
            setActionTrackerCategoryL0Selected(actionTrackerCategoryL0Options);
            isCategoryL0FilterInitialized.current = true;
        }

        if (!isCategoryL1FilterInitialized.current && actionTrackerCategoryL1Options.length > 0) {
            setActionTrackerCategoryL1Selected(actionTrackerCategoryL1Options);
            isCategoryL1FilterInitialized.current = true;
        }

        if (!isOwnerFilterInitialized.current && actionTrackerOwnerOptions.length > 0) {
            setActionTrackerOwnerSelected(actionTrackerOwnerOptions);
            isOwnerFilterInitialized.current = true;
        }

        if (!isStatusFilterInitialized.current && actionTrackerStatusOptions.length > 0) {
            setActionTrackerStatusSelected(actionTrackerStatusOptions);
            isStatusFilterInitialized.current = true;
        }
    }, [
        actionTrackerSectorOptions,
        actionTrackerCategoryL0Options,
        actionTrackerCategoryL1Options,
        actionTrackerOwnerOptions,
        actionTrackerStatusOptions,
    ]);

    useEffect(() => {
        const ensureValidSelection = (selected, options, setter) => {
            const selectedList = Array.isArray(selected) ? selected : [];
            const optionValues = (options || []).map(getFilterValue);
            const validSelections = selectedList.filter((item) => optionValues.includes(getFilterValue(item)));

            if (validSelections.length !== selectedList.length) {
                setter(validSelections);
            }
        };

        if (actionTrackerSectorOptions.length > 0) {
            ensureValidSelection(actionTrackerSectorSelected, actionTrackerSectorOptions, setActionTrackerSectorSelected);
        }
        if (actionTrackerCategoryL0Options.length > 0) {
            ensureValidSelection(actionTrackerCategoryL0Selected, actionTrackerCategoryL0Options, setActionTrackerCategoryL0Selected);
        }
        if (actionTrackerCategoryL1Options.length > 0) {
            ensureValidSelection(actionTrackerCategoryL1Selected, actionTrackerCategoryL1Options, setActionTrackerCategoryL1Selected);
        }
        if (actionTrackerOwnerOptions.length > 0) {
            ensureValidSelection(actionTrackerOwnerSelected, actionTrackerOwnerOptions, setActionTrackerOwnerSelected);
        }
        if (actionTrackerStatusOptions.length > 0) {
            ensureValidSelection(actionTrackerStatusSelected, actionTrackerStatusOptions, setActionTrackerStatusSelected);
        }
    }, [
        actionTrackerSelectedYear,
        actionTrackerSectorOptions,
        actionTrackerCategoryL0Options,
        actionTrackerCategoryL1Options,
        actionTrackerOwnerOptions,
        actionTrackerStatusOptions,
    ]);

    const getForumStepUrl = (forumType, section, key) =>
        categoryForumConfigData?.[forumType]?.[section]?.[key];

    const hasForumTableConfig = tableColumns.length > 0;

    const strategyCouncilSteps = [
        {
            id: 1,
            title: "Step 1: Prepare for the Forum",
            description: "Review the annual Strategy Council calendar, understand who should attend based on SOA category/sector, and prepare for your category forum.",
            bullets: [
                // "View calendar spreadsheet.",
                // "Check planning checklist, invite/approval matrices.",
                // "Download presentation templates."
            ],
            buttons: [
                // { text: "Calendar spreadsheet", icon: "calendar_today", url: getForumStepUrl("strategyCouncil", "planForum", "calendarSpreadsheet") },
                // { text: "View planning checklist", icon: "fact_check", url: getForumStepUrl("strategyCouncil", "planForum", "planningChecklist") },
                // { text: "View invite/approval matrix", icon: "verified_user", url: getForumStepUrl("strategyCouncil", "planForum", "inviteApprovalMatrix") },
                // { text: "Download Presentation Guidance", icon: "download", url: getForumStepUrl("strategyCouncil", "planForum", "downloadTemplateDeck") }
            ]
        },
        {
            id: 2,
            title: "Step 2: Upload your Presentation",
            description: "Prepare and upload your Strategy Council Presentation and any supporting raw data to the right SharePoint folder.",
            bullets: [
                "Upload your presentation and supporting data.",
                {
                    parts: [
                        { text: "Please name your file using the format ", bold: false },
                        { text: "[Filename]_[Sector]_[BU]_[Category]_[ForumMonth]_[ForumYear]", bold: true },
                        { text: " (e.g., ProjectUpdate_Energy_NA_Report_Strategy_04_2026) to ensure it is correctly categorized.", bold: false }
                    ]
                }
            ],
            buttons: [
                // { text: "Upload SC presentation", icon: "upload", url: getForumStepUrl("strategyCouncil", "prepareMaterials", "scPresentation") },
                // { text: "Upload raw Data", icon: "upload", url: getForumStepUrl("strategyCouncil", "prepareMaterials", "rawData") }
            ]
        },
        {
            id: 3,
            title: "Post-forum Executive Summary (Admin Only)",
            adminOnly: true,
            description: "After the Category Forum, the Admin documents key decisions, risks, and next steps in the Executive Summary.",
            bullets: [
                // "This step is completed by the Admin after the forum is conducted. The Admin documents key decisions, risks, and next steps.",
                {
                    parts: [
                        // { text: "To view the Executive Summary for your Strategy Council, Contact : ", bold: false },
                        // { text: "Rachel.Thomas@pepsico.com", href: "mailto:Rachel.Thomas@pepsico.com" },
                        // { text: " or ", bold: false },
                        // { text: "Rajeet.Sengupta@pepsico.com", href: "mailto:Rajeet.Sengupta@pepsico.com" },
                        // { text: ".", bold: false },
                        { text: "If you have any concerns, please reach out to ", bold: false },
                        { text: "Rachel.Thomas@pepsico.com", href: "mailto:Rachel.Thomas@pepsico.com" },
                        { text: " or ", bold: false },
                        { text: "Rajeet.Sengupta@pepsico.com", href: "mailto:Rajeet.Sengupta@pepsico.com" },
                        { text: ".", bold: false }
                    ]
                }
            ],
            buttons: []
        },
        {
            id: 4,
            title: "Step 3: Review Action Items",
            description: "Review the status of action items decided during the Category Forum.",
            bullets: [
                "Click on <b>Open Action Items Tracker</b> to review the action items against your Contract Board forum.",
                {
                    parts: [
                        { text: "Reach out to ", bold: false },
                        { text: "globalprocurementexcellence@pepsico.com", href: "mailto:globalprocurementexcellence@pepsico.com" },
                        { text: " to update/modify your action items, if required. ", bold: false },
                    ]
                }
            ],

        },
        {
            id: 5,
            title: "Step 4: Category Outcomes Tracking",
            description: "Track and update Strategy Council outcomes over the next 3-6 months, including execution, achievement, risks, and follow-through.",
            bullets: [
                "Upload tracking templates for benefits and supplier performance.",
                {
                    parts: [
                        { text: "Please name your file using the format ", bold: false },
                        { text: "[Filename]_[Sector]_[BU]_[Category]_[ForumMonth]_[ForumYear]", bold: true },
                        { text: " (e.g., ProjectUpdate_Energy_NA_Report_Strategy_04_2026) to ensure it is correctly categorized.", bold: false }
                    ]
                }
            ],
            buttons: [
                // { text: "Download Outcomes Tracking Template", icon: "download", url: getForumStepUrl("strategyCouncil", "trackOutcomes", "downloadTemplate") },
                // { text: "Upload Outcomes Tracking Document", icon: "upload", url: getForumStepUrl("strategyCouncil", "trackOutcomes", "uploadDocument") }
            ]
        }
    ];

    // Steps data for Contract Board
    const contractBoardSteps = [
        {
            id: 1,
            title: "Step 1: Prepare for the Forum",
            description: "Prepare for your Category Forum by understanding the Presentation requirements and Approval Matrix",
            bullets: [
                "View <b>Schedule of Authority & Approval Matrix</b>",
                "Download <b>Presentation Guidance</b>",
            ],
            buttons: [
                // { text: "Calendar spreadsheet", icon: "calendar_today", url: getForumStepUrl("contractBoard", "planForum", "calendarSpreadsheet") },
                // { text: "View planning checklist", icon: "fact_check", url: getForumStepUrl("contractBoard", "planForum", "planningChecklist") },
                { text: "Schedule of Authority & Approval Matrix", icon: "", url: getForumStepUrl("contractBoard", "planForum", "inviteApprovalMatrix") },
                { text: "Download Presentation Guidance", icon: "download", url: getForumStepUrl("contractBoard", "planForum", "downloadTemplateDeck") }
            ]
        },
        {
            id: 2,
            title: "Step 2: Upload your Presentation",
            description: "Prepare and upload your <b>Contract Board Presentation</b> and any supporting data/documents.",
            bullets: [
                "Upload your presentation and supporting data.",
                {
                    parts: [
                        // { text: "Prepare and upload your Contract Board Presentation and any supporting data/documents. ", bold: false },
                        // { text: "Upload your presentation and supporting data directly to SharePoint. ", bold: false },
                        { text: "Please name your files using the format: ", bold: false },
                        { text: "[Filetype]_[Sector]_[Category L1]_[ForumMonth_Year]", bold: true },
                        { text: " (e.g., Presentation_APAC_Packaging_05_2026) to ensure it is correctly categorized.", bold: false },
                    ]
                },
                {
                    parts: [
                        { text: "If you want to make any updates to the presentation document, please reach out to ", bold: false },
                        { text: "Rachel.Thomas@pepsico.com", href: "mailto:Rachel.Thomas@pepsico.com" },
                        { text: " or ", bold: false },
                        { text: "Rajeet.Sengupta@pepsico.com", href: "mailto:Rajeet.Sengupta@pepsico.com" },
                        { text: ".", bold: false }
                    ]
                }
            ],
            buttons: [
                { text: "Upload CB presentation (Mandatory)", icon: "upload", url: getForumStepUrl("contractBoard", "prepareMaterials", "cbPresentation") },
                { text: "Upload Supporting Data / Documents (Optional)", icon: "upload", url: getForumStepUrl("contractBoard", "prepareMaterials", "rawData") }
            ]
        },
        {
            id: 3,
            title: "Post-forum Executive Summary (Admin Only)",
            adminOnly: true,
            description: "After the Category Forum is conducted, the Admin will document key decisions, risks, and next steps in the <b>Executive Summary</b><br/>and will share it with all the attendees of the forum via email within a week of conducting the Forum.",
            bullets: [
                // "This step is completed by the Admin after the forum is conducted. The Admin documents key decisions, risks, and next steps.",
                // { parts: [
                // { text: "To view the Executive Summary for your Strategy Council, Contact : ", bold: false },
                // { text: "Rachel.Thomas@pepsico.com", href: "mailto:Rachel.Thomas@pepsico.com" },
                // { text: " or ", bold: false },
                // { text: "Rajeet.Sengupta@pepsico.com", href: "mailto:Rajeet.Sengupta@pepsico.com" },
                // { text: ".", bold: false },

                // ]},
                {
                    parts: [
                        { text: "If you have any concerns, please reach out to ", bold: false },
                        { text: "Rachel.Thomas@pepsico.com", href: "mailto:Rachel.Thomas@pepsico.com" },
                        { text: " or ", bold: false },
                        { text: "Rajeet.Sengupta@pepsico.com", href: "mailto:Rajeet.Sengupta@pepsico.com" },
                        { text: ".", bold: false }
                    ]
                }
            ],
            buttons: []
        },
        {
            id: 4,
            title: "Step 3: Review Action Items",
            description: "Review the status of action items decided during the Category Forum.",
            bullets: [
                "Click on <b>Open Action Items Tracker</b> to review the action items against your Contract Board forum.",
                {
                    parts: [
                        { text: "Reach out to ", bold: false },
                        { text: "globalprocurementexcellence@pepsico.com", href: "mailto:globalprocurementexcellence@pepsico.com" },
                        { text: " to update/modify your action items, if required. ", bold: false },
                    ]
                }
            ],
            buttons: [
                { text: "Open Action Items Tracker", icon: "open_in_new", url: getForumStepUrl("contractBoard", "actionItemsTracking", "goToSharepoint") },
            ]
        },
        {
            id: 5,
            title: "Step 4: Category Outcomes Tracking",
            description: "Track and update Contract Board outcomes over the next 3-6 months, including execution, achievement, risks, and follow-through.",
            bullets: [
                // "Upload tracking templates for benefits and supplier performance.",
                "Upload <b>Outcomes Tracking Document</b> as per the provided template. ",
                {
                    parts: [
                        // { text: "Upload Outcomes Tracking Document as per the provided template. ", bold: false },
                        { text: "Please name your files using the format: ", bold: false },
                        { text: "[Filetype]_[Sector]_[Category L1]_[ForumMonth_Year] ", bold: true },
                        { text: " (e.g., Outcomes Tracking_APAC_Packaging_05_2026) to ensure it is correctly categorized.", bold: false }
                    ]
                }
            ],
            buttons: [
                { text: "Download Outcomes Tracking Template", icon: "download", url: getForumStepUrl("contractBoard", "trackOutcomes", "downloadTemplate") },
                { text: "Upload Outcomes Tracking Document", icon: "upload", url: getForumStepUrl("contractBoard", "trackOutcomes", "uploadDocument") }
            ]
        }
    ];

    // Get current steps based on switcher selection
    const currentSteps = switcherIndex === 1 ? strategyCouncilSteps : contractBoardSteps;
    const currentStepData = currentSteps[selectedStep];
    const isStrategyStepDisabled = () => switcherIndex === 1;

     const loadOutcomeSummaryData = (overrides = {}) => {
        const payload = {
            sector: (overrides.sector || outcomeSummarySectorSelected || []).map(getFilterValue).filter(Boolean),
            l0: (overrides.categoryL0 || outcomeSummaryCategoryL0Selected || []).map(getFilterValue).filter(Boolean),
            l1: (overrides.categoryL1 || outcomeSummaryCategoryL1Selected || []).map(getFilterValue).filter(Boolean),
            presenter: (overrides.presenter || outcomeSummaryPresenterSelected || []).map(getFilterValue).filter(Boolean),
            fromDate: overrides.fromDate ?? (outcomeSummaryFromDateSelected?.[0]?.value || null),
            toDate: overrides.toDate ?? (outcomeSummaryToDateSelected?.[0]?.value || null)
        };
        dispatch(FetchCategoryForumOutcomeSummaryTabularDataAction(payload));
    }

      /* istanbul ignore next */
        const generateMonthObjectsFromLabels = (minLabel, maxLabel) => {
            const result = [];
            if (!minLabel || !maxLabel) return result;
            const start = new Date(`${minLabel} 01`);
            const end = new Date(`${maxLabel} 01`);
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
            return result;
            const monthsDiff =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());
            if (monthsDiff < 0) return result;
            const formatter = new Intl.DateTimeFormat("en-US", {
            month: "short",
            year: "numeric"
            });
            for (let i = 0; i <= monthsDiff; i++) {
            const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
            const year = d.getFullYear();
            const month = d.getMonth() + 1;
            const displayText = formatter.format(d);
            const paddedMonth = String(month).padStart(2, "0");
            result.push({
                id: `${year}-${month}`,
                displayText,
                label: displayText,
                value: `${year}-${paddedMonth}-01`
            });
            }
            return result;
        };

        // Utility to ensure 4-digit years in date labels (e.g., "May 25" -> "May 2025")
        function ensureFourDigitYear(label) {
            if (!label) return label;
            const trimmedLabel = label.trim();
            const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");

            if (lastSpaceIndex === -1) {
                return label;
            }

            const month = trimmedLabel.slice(0, lastSpaceIndex);
            const year = trimmedLabel.slice(lastSpaceIndex + 1);

            if (year.length === 2 && /^\d{2}$/.test(year)) {
                return `${month} 20${year}`;
            }

            return label;
        }

        /* istanbul ignore next */
        const changeFromDateOptionsUtil = (toDateMonthSelected, outcomeSummaryEntireMonthList) => {
            const toDateSelected = toDateMonthSelected.displayText;
            const maxDate = new Date(toDateSelected);
            const filteredMonths = outcomeSummaryEntireMonthList.filter((month) => {
                const monthDate = new Date(month.value);
                return monthDate <= maxDate;
            });
            filteredMonths.push(toDateMonthSelected);
            filteredMonths.sort((a, b) => new Date(a.value) - new Date(b.value));
            return filteredMonths;
        }
        /* istanbul ignore next */
        const changeToDateOptionsUtil = (fromDateMonthSelected,outcomeSummaryEntireMonthList) => {
            
            const toDateSelected1 = fromDateMonthSelected.displayText;
            const fromDate = new Date(toDateSelected1);
            const filteredMonths = outcomeSummaryEntireMonthList.filter((month) => {
                const monthDate = new Date(month.value);
                return monthDate >= fromDate;
            });
            return filteredMonths;
        };

    /* istanbul ignore next */
    const monthLabelToObject = label => {
        if (!label) return null;
        const d = new Date(`${label} 01`);
        if (Number.isNaN(d.getTime())) return null;
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const paddedMonth = String(month).padStart(2, "0");
        return {
        displayText: label,
        id: `${year}-${month}`,
        label,
        value: `${year}-${paddedMonth}-01`
        };
    };

    /* istanbul ignore next */
    const generateMonthObjects = (from, to) => {
        const result = [];

        const start = new Date(
        Number(from.slice(0, 4)),
        Number(from.slice(4, 6)) - 1,
        1
        );
        const end = new Date(Number(to.slice(0, 4)), Number(to.slice(4, 6)) - 1, 1);

        // If start is after end, return empty list
        const monthsDiff =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
        if (monthsDiff < 0) return result;

        const formatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric"
        });

        for (let i = 0; i <= monthsDiff; i++) {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const displayText = formatter.format(d);
        const paddedMonth = String(month).padStart(2, "0");

        result.push({
            id: `${year}-${month}`, // e.g., 2025-3
            displayText, // e.g., Mar 2025
            label: displayText, // e.g., Mar 2025
            value: `${year}-${paddedMonth}-01` // e.g., 2025-03-01
        });
        }

        return result;
    };

    // Initialize OutcomeS ummary ranges based on current type selection
    const initializeOutcomeSummaryDateRanges = () => {

        const dateInfo = categoryForumOutcomeSummaryFiltersData?.dateInfo;
        
        if (!dateInfo) return null;
        
        const { minYearMonth, maxYearMonth, defaultMin, defaultMax } = dateInfo;
        const months = generateMonthObjectsFromLabels(
        ensureFourDigitYear(minYearMonth),
        ensureFourDigitYear(maxYearMonth)
        );
        if (!months.length) return null;
        
        setOutcomeSummaryEntireMonthList(months);
        const defaultFrom = monthLabelToObject(ensureFourDigitYear(defaultMin)) || months[0];
        const defaultTo = monthLabelToObject(ensureFourDigitYear(defaultMax)) || months[months.length - 1];
        
        setOutcomeSummaryFromDates(changeFromDateOptionsUtil(defaultTo, months));
        setOutcomeSummaryToDates(changeToDateOptionsUtil(defaultFrom, months));
        setOutcomeSummaryFromDateSelected([defaultFrom]);
        setOutcomeSummaryToDateSelected([defaultTo]);
       
        localStorage.setItem("outcomeSummary-fromDate", JSON.stringify([defaultFrom]));
        localStorage.setItem("outcomeSummary-toDate", JSON.stringify([defaultTo]));

        return { defaultFrom, defaultTo };
    };

    if (authState?.isAuthenticated) {
        return (
            <Layout>
                <div style={{ display: "flex" }}>
                    <Content style={{ padding: "0 30px 30px 100px", background: "#D7E5F8", minHeight: "125vh" }}>
                        {Object.keys(userInfo)?.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "calc(100vh - 64px)" }}>
                                <LoadingSpinner size={{ height: "80px", width: "80px" }} />
                            </div>
                        ) : (
                            <>
                                {isUserDataFetched || isConfigLoader ? (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "calc(100vh - 64px)" }}>
                                        <LoadingSpinner size={{ height: "80px", width: "80px" }} />
                                    </div>
                                ) : (
                                    <div className="category-forums-container" ref={categoryForumsContractBoardRef}>
                                        {/* Banner */}
                                        <CommodityPageHeader
                                            header={"Category Forums"}
                                            subtext={"Global governance forums aligning category strategy and contracting-sourcing outcomes"}
                                            image={NewCategoryForum}
                                            pageName={"CategoryForums"}
                                            isCategoryForumPage={true}
                                            cfLastUpdateValue={CategoryForumsSwitcherIndex == outcomesSummaryTabIndex ? categoryForumOutcomeSummaryTabularData?.dataAsOn : CategoryForumsSwitcherIndex == actionTrackerTabIndex ? actionTrackerLastUpdate : lastUpdateContractBoard}
                                            cfRefreshFrequencyValue={CategoryForumsSwitcherIndex == outcomesSummaryTabIndex ? categoryForumOutcomeSummaryTabularData?.refreshFrequency : CategoryForumsSwitcherIndex == actionTrackerTabIndex ? actionTrackerRefreshFrequency : refreshFrequencyContractBoard}
                                        />

                                        {/* ONE Main White Card - wraps everything below banner */}
                                        <div>
                                            <div className="forums-main-card">
                                                <Switcher
                                                    items={[
                                                        // { label: "Overview of Category Forums", style: { width: '100%', display: 'flex', justifyContent: 'center' } },
                                                        { label: (
                                                            <Tooltip direction="top-middle"
                                                                    text={"Provides an overview of Category Forums, including the forum types, annual forum calendar with key information for upcoming and scheduled forums."}>
                                                                <span style={{ cursor: 'pointer' }}>Overview of Category Forums</span>
                                                            </Tooltip>
                                                        ), style: { width: '100%', display: 'flex', justifyContent: 'center' } },
                                                        ...(showYourCategoryForums
                                                            ? [{
                                                                label: (
                                                                    <Tooltip direction="top-middle"
                                                                            text={"View the Contract Board meetings assigned to you, understand the end-to-end forum workflow, prepare and conduct all forum-related activities."}>
                                                                        <span style={{ cursor: 'pointer' }}>Your Category Forums</span>
                                                                    </Tooltip>
                                                                ), style: { width: '100%', display: 'flex', justifyContent: 'center' }
                                                            }]
                                                            : []),
                                                        { label: (
                                                            <Tooltip direction="top-middle"
                                                                    text={"Provides a consolidated view of Contract Board meetings, including KPIs, statuses and recommendations."}>
                                                                <span style={{ cursor: 'pointer' }}>Contract Boards Dashboard</span>
                                                            </Tooltip>
                                                        ), style: { width: '100%', display: 'flex', justifyContent: 'center' } },
                                                        // { label: "Contract Boards Outcomes Summary ( Data Not Available )", disabled: true, style: { width: '100%', display: 'flex', justifyContent: 'center', color: '#c2bcbc', pointerEvents: 'none' } },
                                                        { label: (
                                                            <Tooltip direction="top-middle"
                                                                    text={"Provides a centralized view of all action items agreed during Contract Board meetings, including owners and action statuses."}>
                                                                <span style={{ cursor: 'pointer' }}>Contract Boards Action Tracker</span>
                                                            </Tooltip>
                                                        ), style: { width: '100%', display: 'flex', justifyContent: 'center' } },
                                                        // { label: categoryForumOutcomeSummaryFiltersData?.showData ? "Contract Boards Outcomes Summary" : "Contract Boards Outcomes Summary (Data not Available)", disabled: !categoryForumOutcomeSummaryFiltersData?.showData, style: { width: '100%', display: 'flex', justifyContent: 'center', color: !categoryForumOutcomeSummaryFiltersData?.showData ? '#c2bcbc' : '', pointerEvents: !categoryForumOutcomeSummaryFiltersData?.showData ? 'none' : 'auto' } }
                                                        { label: (
                                                            <Tooltip direction="top-middle"
                                                                    text={"Provides a summarized view of Contract Board outcomes and their achievement levels against targets (MDOs/LDOs)"}>
                                                                <span style={{ cursor: 'pointer' }}>{categoryForumOutcomeSummaryFiltersData?.showData ? "Contract Boards Outcomes Summary" : "Contract Boards Outcomes Summary (Data not Available)"}</span>
                                                            </Tooltip>
                                                        ), disabled: !categoryForumOutcomeSummaryFiltersData?.showData, style: { width: '100%', display: 'flex', justifyContent: 'center', color: !categoryForumOutcomeSummaryFiltersData?.showData ? '#c2bcbc' : '' } }
                                                    ]}
                                                    selectedItemIndex={CategoryForumsSwitcherIndex}
                                                    onUpdate={(val) => {
                                                        setCategoryForumsSwitcherIndex(val);
                                                        // setSelectedStep(0);
                                                    }}
                                                    style={{ width: '100%', marginBottom: '24px' }}
                                                />

                                                {
                                                    CategoryForumsSwitcherIndex == overviewTabIndex && (
                                                        <>

                                                            {/* Description Section */}
                                                            <div className="category-forums-description-title">Overview of Category Forums</div>
                                                            <div className="forums-description-section">
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <div>
                                                                        <p className="forums-description-text">
                                                                            Global governance forums that align procurement strategy and contracts with business priorities, ensuring<br />consistent and competitive sourcing outcomes.
                                                                        </p>
                                                                        <p className="forums-description-bold">
                                                                            Both forums are governed by Procurement Excellence.
                                                                        </p>
                                                                    </div>
                                                                   
                                                                </div>


                                                                <div className="forums-links">
                                                                    {/* <span className="forums-link-text">FAQs</span> */}
                                                                    <span className="forum-know-more" onClick={() => setOpenKnowMoreModal(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenKnowMoreModal(true)}>Know more</span>
                                                                </div>
                                                            </div>

                                                            <Row gutter={[20, 20]} align="top">
                                                                <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ minWidth: 0 }}>
                                                                    <div className="forums-left-card" style={{background: '#F8F8F8'}}>
                                                                        <div className="forum-info-header">
                                                                            <Icon icon="developer_board" size="medium" style={{ color: '#FCAC4B' }} />
                                                                            <span className="forum-info-title">Contract Board</span>
                                                                        </div>
                                                                        <p className="forum-info-description">
                                                                            Global forum where Category Leads present proposed bidding techniques, commercial terms, and contract recommendations for governance and consistency.
                                                                        </p>
                                                                        <p className="forum-info-cadence">
                                                                            <strong>{"Cadence: Bi\u2011weekly, with special sessions as needed."}</strong>
                                                                        </p>
                                                                        <div className="forum-info-links" style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'center' }}>
                                                                            <span className="forum-know-more" onClick={() => setOpenContractBoardKnowMoreModal(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenContractBoardKnowMoreModal(true)}>Know more</span>
                                                                            <span className="forum-know-more" onClick={() => setOpenContractBoardFAQsModal(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenContractBoardFAQsModal(true)}>FAQs</span>
                                                                            <Button
                                                                                text="Request Contract Board Meeting"
                                                                                variant="secondary"
                                                                                iconLeading="add"
                                                                                size="small"
                                                                                onClick={() => { window.open(categoryForumConfigData?.newCategoryForum, "_blank") }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ minWidth: 0 }}>
                                                                    <div className="forums-left-card" style={{background: '#F8F8F8'}}>
                                                                        <div className="forum-info-header">
                                                                            <Icon icon="data_exploration" size="medium" style={{ color: '#60A3FC' }} />
                                                                            <span className="forum-info-title">Strategy Council</span>
                                                                        </div>
                                                                        <p className="forum-info-description">
                                                                            Global governance forums that align procurement strategy and contracts with business priorities, ensuring consistent and competitive sourcing outcomes.
                                                                        </p>
                                                                        <p className="forum-info-cadence">
                                                                            <strong>Cadence: Annual per category.</strong>
                                                                        </p>
                                                                        <div className="forum-info-links" style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'center' }}>
                                                                            <span className="forum-know-more" onClick={() => setOpenStrategyCouncilKnowMoreModal(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenStrategyCouncilKnowMoreModal(true)}>Know more</span>
                                                                            <span className="forum-strategy-council-knowmore-faqs">FAQs</span>
                                                                            <Button
                                                                                text="Request Strategy Council Meeting"
                                                                                variant="secondary"
                                                                                iconLeading="add"
                                                                                size="small"
                                                                                // onClick={() => { window.open(categoryForumConfigData?.newCategoryForum, "_blank") }}
                                                                                disabled={true}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row gutter={[20, 20]} align="top" style={{ marginTop: '16px' }}>
                                                                {/* Left Column - Switcher Card */}


                                                                {/* Right Column - 12 Month Forums Table Card */}
                                                                <Col xs={24} sm={24} md={24} lg={24} xl={24} style={{ minWidth: 0 }}>
                                                                    <div className="forums-table-card">
                                                                        <div ref={cfContractBoardTableRef}>
                                                                            <div className="forums-table-card-header">
                                                                                <div>
                                                                                    <h3 className="forums-table-title">Annual Category Forums Calendar</h3>
                                                                                    <p className="forums-table-subtitle">Scheduled Strategy Councils and Contract Boards for the selected calendar year.</p>
                                                                                </div>
                                                                                <div className="calendar-actions">
                                                                                    {/* Download button */}
                                                                                    <DropdownWithIcons
                                                                                        modalRef={cfContractBoardTableRef}
                                                                                        kpiName={`Category_Forums_Calendar_${selectedYear}`}
                                                                                        isTable={true}
                                                                                        checkPDFDownloaded={checkPDFDownloaded}
                                                                                    />
                                                                                    {/* Year switcher */}
                                                                                    <Switcher
                                                                                        items={[
                                                                                            { label: String(new Date().getFullYear() - 1) },
                                                                                            { label: String(new Date().getFullYear()) },
                                                                                            { label: String(new Date().getFullYear() + 1) },
                                                                                        ]}
                                                                                        selectedItemIndex={selectedYear - (new Date().getFullYear() - 1)}
                                                                                        onUpdate={(idx) => setSelectedYear(new Date().getFullYear() - 1 + idx)}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            {/* {renderLegendMarkup(legendItems, "cf-calendar")} */}
                                                                            <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                                                                                {legendItems?.length > 0 && <span style={{margin: '0 10px 10px 0'}} className="cf-outcomes-summary-legend-label">Legend</span>}
                                                                                {
                                                                                    legendItems?.map((legendItem) => (
                                                                                        <span
                                                                                            key={legendItem.label}
                                                                                            style={{
                                                                                                display: 'inline-block',
                                                                                                padding: '4px 10px',
                                                                                                borderRadius: '5px',
                                                                                                margin: '0 5px 10px 0',
                                                                                                background: legendItem.color,
                                                                                                border: `1px solid ${legendItem.strokeColor}`,
                                                                                                color: '#111111',
                                                                                                // fontSize: '12px',
                                                                                                // lineHeight: 1.2,
                                                                                                whiteSpace: 'nowrap',
                                                                                            }}
                                                                                            className="cf-outcomes-summary-legend-label"
                                                                                        >
                                                                                            {legendItem.label}
                                                                                        </span>
                                                                                    ))
                                                                                }
                                                                            </div>
                                                                            <div className="above-forums-table">
                                                                                {isTableLoader ? (
                                                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                                                                        <LoadingSpinner size={{ height: '48px', width: '48px' }} />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="forums-table-container">
                                                                                        {/* <div className="forums-style-table"> */}
                                                                                        {hasForumTableConfig && (
                                                                                            <DataTable
                                                                                                columns={tableColumns}
                                                                                                data={categoryForumTableData}
                                                                                                enableSorting={false}
                                                                                                showColumnFilters={false}
                                                                                                tableHeader=""
                                                                                                tableDescription=""
                                                                                                pageSize={categoryForumTableData?.length}
                                                                                                pageIndex={0}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <br />

                                                            {/* Need a New Forum - Nested card inside main card */}
                                                           

                                                            {/* Left + Right Cards Row */}
                                                          

                                                            {
                                                                openKnowMoreModal && (
                                                                    <>
                                                                        <Modal
                                                                            closeOnOutsideClick={true}
                                                                            onCloseModal={function oFe() { setOpenKnowMoreModal(false) }}
                                                                            showModal={true}
                                                                            showLink={false}
                                                                            showPrimaryButton={false}
                                                                            showSecondaryButton={false}
                                                                            showTertiaryButton={false}
                                                                            showAuxiliaryButton={false}
                                                                            type="S"
                                                                            className="know-more-modal"
                                                                            title={
                                                                                <>
                                                                                    <div>
                                                                                        <span style={{ marginRight: '1rem' }}><img src={ForumKnowMoreModalicon} alt="Know More Icon" /></span>
                                                                                        <span className="knowmore-modal-header">Know more: Category Forums</span>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        >

                                                                            <div style={{ height: '100%' }}>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>Strategy Councils and Contract Boards are global category governance forums that operationalize the Procurement Operating Model within the 7-step sourcing process.</div>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>They strengthen integration between global categories, regions, and business units, and sharpen our collective procurement capabilities.</div>
                                                                                <div className="knowmore-modal-bold-content">Both forums are governed by Procurement Excellence.</div>
                                                                            </div>

                                                                        </Modal>
                                                                    </>
                                                                )
                                                            }

                                                            {
                                                                openStrategyCouncilKnowMoreModal && (
                                                                    <>
                                                                        <Modal

                                                                            closeOnOutsideClick={() => setOpenStrategyCouncilKnowMoreModal(false)}
                                                                            onCloseModal={function oFe() { setOpenStrategyCouncilKnowMoreModal(false) }}
                                                                            showModal={true}
                                                                            showLink={false}
                                                                            showPrimaryButton={false}
                                                                            showSecondaryButton={false}
                                                                            showTertiaryButton={false}
                                                                            showAuxiliaryButton={false}
                                                                            type="S"
                                                                            className="know-more-strategy-council-modal"
                                                                            title={
                                                                                <>

                                                                                    <div style={{ display: 'flex' }}>
                                                                                        <span style={{ marginRight: '1rem' }}>
                                                                                            <Icon
                                                                                                icon={"data_exploration"}
                                                                                                size="medium"
                                                                                                style={{ color: '#60A3FC', fontSize: '25px' }}
                                                                                            />
                                                                                        </span>
                                                                                        <span className="knowmore-modal-header">Know more: Strategy Council</span>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        >

                                                                            <div style={{ height: '100%' }}>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>The Strategy Council requires Category Leads to present a co-created procurement strategy to a board comprised of Schedule of Authority (SOA) members and key cross-functional stakeholders.</div>
                                                                                <div className="knowmore-modal-bold-content">Each Session:</div>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>
                                                                                    <ul>
                                                                                        <li>Ensures procurement strategy is aligned to business objectives.</li>
                                                                                        <li>Addresses major market challenges and opportunities.</li>
                                                                                        <li>Drives strategic input and guidance from business and functional leaders.</li>
                                                                                    </ul>
                                                                                </div>
                                                                                <div  ><span className="knowmore-modal-bold-content">Cadence:</span><span className="knowmore-modal-content"> Annual per category, to stay aligned with evolving market dynamics and business priorities.</span></div>
                                                                                {/* <div  style={{marginBottom: '1rem'}}><span className="knowmore-modal-bold-content">Timing:</span><span className="knowmore-modal-content"> Presentations typically occur at least 12 months ahead of contract approval, or as aligned with category nuances.</span></div> */}

                                                                            </div>

                                                                        </Modal>
                                                                    </>
                                                                )
                                                            }

                                                            {
                                                                openContractBoardKnowMoreModal && (
                                                                    <>
                                                                        <Modal

                                                                            closeOnOutsideClick={() => setOpenContractBoardKnowMoreModal(false)}
                                                                            onCloseModal={function oFe() { setOpenContractBoardKnowMoreModal(false) }}
                                                                            showModal={true}
                                                                            showLink={false}
                                                                            showPrimaryButton={false}
                                                                            showSecondaryButton={false}
                                                                            showTertiaryButton={false}
                                                                            showAuxiliaryButton={false}
                                                                            type="S"
                                                                            className="know-more-contract-board-modal"
                                                                            title={
                                                                                <>

                                                                                    <div>
                                                                                        <span style={{ marginRight: '1rem' }}><img src={ForumContractBoardKnowMoreIcon} alt="Know More Icon" /></span>
                                                                                        <span className="knowmore-modal-header">Know more: Contract Board</span>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        >

                                                                            <div style={{ height: '100%' }}>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>The Contract Board requires Category Leads to present the proposed bidding technique, desired commercial and contractual terms, and how the recommendation ensures competitiveness for PepsiCo to the SOA driven Contract Board consisting of Procurement Leaders and key stakeholders.</div>
                                                                                <div className="knowmore-modal-bold-content">Each Session:</div>
                                                                                <div className="knowmore-modal-content" style={{ marginBottom: '1rem' }}>
                                                                                    <ul>
                                                                                        <li>Provides early visibility and insight to the board (prior to bid launch/negotiation).</li>
                                                                                        <li>Enables timely and informed guidance.</li>
                                                                                        <li>Strengthens governance and consistency across sourcing outcomes.</li>
                                                                                    </ul>
                                                                                </div>
                                                                                <div  ><span className="knowmore-modal-bold-content">Cadence:</span><span className="knowmore-modal-content"> Bi-weekly board meetings, with special sessions scheduled as needed.</span></div>
                                                                                <div style={{ marginBottom: '1rem' }}><span className="knowmore-modal-bold-content">Timing:</span><span className="knowmore-modal-content"> Presentations typically occur at least 12 months ahead of contract approval, or as aligned with category nuances.</span></div>

                                                                            </div>

                                                                        </Modal>
                                                                    </>
                                                                )
                                                            }

                                                            {
                                                                openContractBoardFAQsModal && (
                                                                    <>
                                                                        <Modal
                                                                            closeOnOutsideClick={() => setOpenContractBoardFAQsModal(false)}
                                                                            onCloseModal={function oFe() { setOpenContractBoardFAQsModal(false) }}
                                                                            showModal={true}
                                                                            showLink={false}
                                                                            showPrimaryButton={false}
                                                                            showSecondaryButton={false}
                                                                            showTertiaryButton={false}
                                                                            showAuxiliaryButton={false}
                                                                            type="S"
                                                                            className="know-more-contract-board-modal"
                                                                            title={
                                                                                <>
                                                                                    <div>
                                                                                        <span style={{ marginRight: '1rem' }}><img src={ForumContractBoardKnowMoreIcon} alt="Know More Icon" /></span>
                                                                                        <span className="knowmore-modal-header">FAQs: Contract Board</span>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        >

                                                                            <div style={{ height: '100%', overflowY: "auto" }}>
                                                                                {categoryForumConfigData?.contractBoard?.faqs?.map((faq) => (
                                                                                    <AccordionItemV2
                                                                                        key={faq.id}
                                                                                        label={faq.question}
                                                                                        presentation="box-shadow"
                                                                                        className="custom-categoryForum-faq-accordion-items"
                                                                                        isOpen={activeId === faq.id} // important
                                                                                        onUpdate={() =>
                                                                                            setActiveId((prev) => (prev === faq.id ? null : faq.id))
                                                                                        }
                                                                                    >
                                                                                        <div style={{
                                                                                            padding: "12px 4px",
                                                                                            color: "#555",
                                                                                            lineHeight: "1.6",
                                                                                            fontSize: "14px"
                                                                                        }}
                                                                                            className="faq-accordion-answer">
                                                                                            {faq.answer}
                                                                                        </div>
                                                                                    </AccordionItemV2>
                                                                                ))}
                                                                            </div>

                                                                        </Modal>
                                                                    </>
                                                                )

                                                            }

                                                        </>
                                                    )
                                                }

                                                {
                                                    showYourCategoryForums && CategoryForumsSwitcherIndex == yourCategoryForumsTabIndex && (
                                                        <>
                                                         <YourCategoryForums categoryForumConfig={categoryForumConfig} />
                                                        </>
                                                    )
                                                }

                                                {
                                                    CategoryForumsSwitcherIndex == dashboardTabIndex && (
                                                        <>
                                                            <div>
                                                                {/* Description Section */}
                                                                <div className="category-forums-description-title" style={{ width: '100%' }}>
                                                                    Contract Boards Dashboard
                                                                    <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                                        <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={categoryForumsContractBoardRef} kpiName={"Contract Boards Dashboard"} isModal={true} />
                                                                    </span>
                                                                </div>
                                                                <div className="forums-description-text" style={{ marginBottom: '1rem' }}>Year-to-date Performance and forward outlook for Contract Boards.</div>

                                                                
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', gap: '1rem', marginBottom: '0.5rem' }}>
                                                                    <span style={{ position: 'relative', top: '7px' }} className="category-forums-kpi-section-title">Year to Date</span>
                                                                    <span>

                                                                        <Switcher
                                                                            items={[
                                                                                { label: String(new Date().getFullYear() - 1) },
                                                                                { label: String(new Date().getFullYear()) },
                                                                                { label: String(new Date().getFullYear() + 1) },
                                                                            ]}
                                                                            selectedItemIndex={contractBoardSelectedYear - (new Date().getFullYear() - 1)}
                                                                            onUpdate={(idx) => setContractBoardSelectedYear(new Date().getFullYear() - 1 + idx)}
                                                                        />
                                                                    </span>
                                                                </div>

                                                                {/* KPI Stats Section - Year to Date and Balance of the Year */}

                                                                {
                                                                    isCategoryForumYearToDateLoading ? (
                                                                        <>
                                                                            <PHKPICard
                                                                                slotContent={<SkeletonList count={3} height={26} />}
                                                                                className={"vr-card"}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="new-forum-card category-forums-kpi-card">
                                                                                {/* Year to Date Section */}
                                                                                <div className="category-forums-kpi-section">
                                                                                    <div className="category-forums-kpi-row">
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{contractBoardForumsCompleted}</div>
                                                                                                <div className="category-forums-kpi-stat-label">Contract Board Forums completed</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                {/* <div className="category-forums-kpi-stat-number">{totalSpendYearToDate}
                                                                                                <span className="category-forums-kpi-stat-unit">mn</span>
                                                                                            </div> */}
                                                                                                <div className="spend-modal-alphanumeric-value-small">
                                                                                                    <div className='spend-modal-numeric-value-small'>{totalSpendYearToDate ? formatNumberWithCommas(totalSpendYearToDate?.['numericValue']) : 0}</div>
                                                                                                    <div className="spend-modal-alpha-value-small">{totalSpendYearToDate?.['unit']}</div>
                                                                                                </div>
                                                                                                <div className="category-forums-kpi-stat-label">Total Spend covered</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{totalSubcategoriesCompletedYearToDate}</div>
                                                                                                <div className="category-forums-kpi-stat-label">L2 Categories completed</div>
                                                                                            </div>
                                                                                            <div className="category-forums-kpi-tags-section category-forums-kpi-tags-wrap">
                                                                                                {yearToDateCategoryGroups.map((item) => (
                                                                                                    <Tooltip
                                                                                                        key={item.category}
                                                                                                        direction="top-middle"
                                                                                                        text={
                                                                                                            <div className="category-forums-kpi-tag-tooltip-content">
                                                                                                                <div className="category-forums-kpi-tag-tooltip-title">{item.category}</div>
                                                                                                                {item.subCategories.length > 0 ? (
                                                                                                                    item.subCategories.map((subCategory) => (
                                                                                                                        <div key={subCategory} className="category-forums-kpi-tag-tooltip-item">
                                                                                                                            • {subCategory}
                                                                                                                        </div>
                                                                                                                    ))
                                                                                                                ) : (
                                                                                                                    // <div className="category-forums-kpi-tag-tooltip-item">• No sub-categories</div>
                                                                                                                    <></>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        }
                                                                                                    >
                                                                                                        <div className="category-forums-kpi-tag">{item.category} ({item.count})</div>
                                                                                                    </Tooltip>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                            </div>
                                                                        </>
                                                                    )
                                                                }

                                                                <div className="category-forums-kpi-section-title">Balance of the Year</div>

                                                                {
                                                                    isCategoryForumBalanceOfTheYearLoading ? (
                                                                        <>
                                                                            <PHKPICard
                                                                                slotContent={<SkeletonList count={3} height={26} />}
                                                                                className={"vr-card"}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="new-forum-card category-forums-kpi-card">
                                                                                {/* Balance of the Year Section */}
                                                                                <div className="category-forums-kpi-section ">
                                                                                    {/* <div className="category-forums-kpi-section-title">Balance of the Year</div> */}
                                                                                    <div className="category-forums-kpi-row">
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{contractBoardForumsScheduled}</div>
                                                                                                <div className="category-forums-kpi-stat-label">Contract Board Forums scheduled</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                {/* <div className="category-forums-kpi-stat-number">{estimatedSpendBalanceOfYear}
                                                                                                    <span className="category-forums-kpi-stat-unit">mn</span>
                                                                                                </div> */}
                                                                                                <div className="spend-modal-alphanumeric-value-small">
                                                                                                    <div className='spend-modal-numeric-value-small'>{estimatedSpendBalanceOfYear ? formatNumberWithCommas(estimatedSpendBalanceOfYear?.['numericValue']) : 0}</div>
                                                                                                    <div className="spend-modal-alpha-value-small">{estimatedSpendBalanceOfYear?.['unit']}</div>
                                                                                                </div>
                                                                                                <div className="category-forums-kpi-stat-label">Estimated Spend to be covered</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{totalSubcategoriesScheduledBalanceOfYear}</div>
                                                                                                <div className="category-forums-kpi-stat-label">L2 Categories scheduled</div>
                                                                                            </div>
                                                                                            <div className="category-forums-kpi-tags-section category-forums-kpi-tags-wrap">
                                                                                                {balanceOfYearCategoryGroups.map((item) => (
                                                                                                    <Tooltip
                                                                                                        key={item.category}
                                                                                                        direction="top-middle"
                                                                                                        text={
                                                                                                            <div className="category-forums-kpi-tag-tooltip-content">
                                                                                                                <div className="category-forums-kpi-tag-tooltip-title">{item.category}</div>
                                                                                                                {item.subCategories.length > 0 ? (
                                                                                                                    item.subCategories.map((subCategory) => (
                                                                                                                        <div key={subCategory} className="category-forums-kpi-tag-tooltip-item">
                                                                                                                            • {subCategory}
                                                                                                                        </div>
                                                                                                                    ))
                                                                                                                ) : (
                                                                                                                    // <div className="category-forums-kpi-tag-tooltip-item">• No sub-categories</div>
                                                                                                                    <></>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        }
                                                                                                    >
                                                                                                        <div className="category-forums-kpi-tag">{item.category} ({item.count})</div>
                                                                                                    </Tooltip>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                            </div>
                                                                        </>
                                                                    )
                                                                }



                                                                <Row gutter={[10, 10]} justify={"start"} style={{ position: 'relative' }}>
                                                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                                                        <div ref={completedVsRemainingRef} style={{ height: '100%' }}>
                                                                            {
                                                                                isCategoryForumSummaryLoading ? (
                                                                                    <>
                                                                                        <PHKPICard
                                                                                            slotContent={<SkeletonList count={13} height="30" />}
                                                                                            className={"vr-card"}
                                                                                        />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Card style={{ height: '100%' }} className="archlet-chart-card">
                                                                                            <div className="category-forums-kpi-section-title" style={{ width: "100%", textAlign: "left" }}>
                                                                                                Total Contract Board Forums: Completed vs Remaining
                                                                                                <span style={{ float: 'right' }}>
                                                                                                    <DropdownWithIcons
                                                                                                        modalRef={completedVsRemainingRef} kpiName={"Total Contract Board Forums: Completed vs Remaining"}
                                                                                                        checkPDFDownloaded={checkPDFDownloaded}
                                                                                                    />
                                                                                                </span>
                                                                                            </div>
                                                                                            {
                                                                                                ContractBoardDonutChartData && ContractBoardDonutChartData?.length > 0 ?
                                                                                                    <>

                                                                                                        {
                                                                                                            <>
                                                                                                                <SpendDonutChart countryData={ContractBoardDonutChartData} isCategoryForums={true} />
                                                                                                            </>
                                                                                                        }
                                                                                                    </>
                                                                                                    :

                                                                                                    <NoData />
                                                                                            }
                                                                                        </Card>
                                                                                    </>
                                                                                )
                                                                            }

                                                                        </div>
                                                                    </Col>

                                                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                                                        <div ref={forumsByMonthRef}>
                                                                            {
                                                                                isCategoryForumForumsByCategoryLoading ? (
                                                                                    <>
                                                                                        <PHKPICard
                                                                                            slotContent={<SkeletonList count={13} height="30" />}
                                                                                            className={"vr-card"}
                                                                                        />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Card style={{ height: '100%' }} className="archlet-chart-card">
                                                                                            <div className="category-forums-kpi-section-title" style={{ width: "100%", textAlign: "left" }}>
                                                                                                Total Contract Board Forums: By Month - By Category
                                                                                                <span style={{ float: 'right' }}>
                                                                                                    <DropdownWithIcons
                                                                                                        modalRef={forumsByMonthRef} kpiName={"Total Contract Board Forums: By Month - By Category"}
                                                                                                        checkPDFDownloaded={checkPDFDownloaded}
                                                                                                    />
                                                                                                </span>
                                                                                            </div>
                                                                                            {
                                                                                                forumsByMonthBarChartData && forumsByMonthBarChartData?.length > 0 ?
                                                                                                    <>

                                                                                                        {
                                                                                                            <>

                                                                                                                <CategoryForumsBarChart linebarchartData={forumsByMonthBarChartData} processedforumsByMonthBarChartLegends={processedforumsByMonthBarChartLegends} />
                                                                                                                {renderLegendMarkup(processedforumsByMonthBarChartLegends, "contractBoard")}

                                                                                                                {/* <LegendList legends={volumeShareByExportingCountryChartColors} /> */}
                                                                                                            </>
                                                                                                        }
                                                                                                    </>
                                                                                                    :

                                                                                                    <NoData />
                                                                                            }
                                                                                        </Card>
                                                                                    </>
                                                                                )
                                                                            }

                                                                        </div>
                                                                    </Col>

                                                                </Row>

                                                                <Row gutter={[10, 10]} style={{ width: '100%' }}>
                                                                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                                                        {
                                                                            isCategoryForumContractBoardsDetailsLoading ? (
                                                                                <>
                                                                                    <PHKPICard
                                                                                        slotContent={<SkeletonList count={8} height="30" />}
                                                                                        className={"vr-card"}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    {
                                                                                        effectiveContractBoardsTableData && effectiveContractBoardsTableData?.length > 0 ?
                                                                                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%' }}>
                                                                                                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', overflow: 'hidden' }}>
                                                                                                    <div className="ph-table-wrapper category-forums-contract-boarddetails-horizontal-scroll" style={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                                                                                                        <CommonTable className={`category-forums-contract-boarddetails-table ${showContractBoardsFolderColumn ? "category-forums-contract-boarddetails-table--with-folder" : "category-forums-contract-boarddetails-table--without-folder"}`} data={effectiveContractBoardsTableData} columns={contractBoardsTableColumns} tableHeader={"Contract Boards details"} enableSorting={true} columnsToInclude={contractBoardsColumnsToInclude} columnMapping={contractBoardsTableColumnMapping} tableName={'Contract Boards details'} />
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            : <Card style={{ marginTop: '1rem' }}><div style={{ width: '100%', textAlign: 'left', fontSize: 18, fontWeight: 600, color: '#2D2D2D', fontFamily: 'var(--Primitives-Typography-Font-Family-Heading, Inter)' }}>Contract Board Details</div><NoData /></Card>
                                                                                    }
                                                                                </>
                                                                            )
                                                                        }
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </>
                                                    )
                                                }

                                                {
                                                    CategoryForumsSwitcherIndex == outcomesSummaryTabIndex && (
                                                        <>
                                                            <div ref={contractBoardsOutcomesSummaryRef}>
                                                                {/* Description Section */}
                                                                <div className="category-forums-description-title" style={{ width: '100%' }}>
                                                                    Contract Boards Outcomes Summary
                                                                    
                                                                </div>
                                                                <div className="forums-description-text" style={{ marginBottom: '1rem' }}>Outcome achievement status of desired objectives aligned during Contract Board meetings.</div>

                                                                
                                                                <div style={{ width: "100%", marginTop: "12px" }}>
                                                                    <Row
                                                                        gutter={[10, 0]}
                                                                        justify="start"
                                                                        wrap={false}
                                                                        className="category-forums-action-tracker-top-filter-row"
                                                                        style={{
                                                                            width: "100%",
                                                                            alignItems: "flex-end",
                                                                            flexWrap: "nowrap"
                                                                        }}
                                                                    >
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Sector"
                                                                                placeholder="Sectors"
                                                                                childList={outcomeSummarySectorList}
                                                                                selectedValue={outcomeSummarySectorSelected}
                                                                                setSelectedValue={onOutcomeSummarySectorChange}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Category L0"
                                                                                placeholder="Categories L0"
                                                                                childList={outcomeSummaryCategoryL0List}
                                                                                selectedValue={outcomeSummaryCategoryL0Selected}
                                                                                setSelectedValue={onOutcomeSummaryCategoryL0Change}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Category L1"
                                                                                placeholder="Categories L1"
                                                                                childList={outcomeSummaryCategoryL1List}
                                                                                selectedValue={outcomeSummaryCategoryL1Selected}
                                                                                setSelectedValue={onOutcomeSummaryCategoryL1Change}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Presenter"
                                                                                placeholder="Presenter"
                                                                                childList={outcomeSummaryPresenterList}
                                                                                selectedValue={outcomeSummaryPresenterSelected}
                                                                                setSelectedValue={onOutcomeSummaryPresenterChange}
                                                                            />
                                                                        </Col>
                                                                       
                                                                        <Col flex="1">
                                                                            <SingleSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="From Month"
                                                                                placeholder="From Month"
                                                                                childList={outcomeSummaryFromDates}
                                                                                selectedValue={outcomeSummaryFromDateSelected}
                                                                                setSelectedValue={onOutcomeSummaryFromDateChange}
                                                                            />
                                                                        </Col>
                                                                         <Col flex="1">
                                                                            <SingleSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="To Month"
                                                                                placeholder="To Month"
                                                                                childList={outcomeSummaryToDates}
                                                                                selectedValue={outcomeSummaryToDateSelected}
                                                                                setSelectedValue={onOutcomeSummaryToDateChange}
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                    <Row justify="end">
                                                                        <button className="reset-button" onClick={onOutcomeSummaryFiltersReset} style={{ marginTop: '12px', marginRight: '0.8rem' }}>
                                                                            <img src={reset_icon} className="reset_icon" alt="Reset Icon" />
                                                                            <span>Reset Filters</span>
                                                                        </button>
                                                                    </Row>
                                                                </div>
                               
                                                            


                                                                <Row gutter={[10, 10]} style={{ width: '100%' }}>
                                                                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                                                        {
                                                                            isOutcomeSummaryTableLoading ? (
                                                                                <>
                                                                                    <PHKPICard
                                                                                        slotContent={<SkeletonList count={8} height="30" />}
                                                                                        className={"vr-card"}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Card ref={contractBoardsOutcomesSummaryRef} style={{ height: '100%', marginTop: '1rem', minHeight: '360px' }} className="category-forums-action-items-card">
                                                                                        <div className="category-forums-action-items-card-header" style={{ width: '100%', textAlign: 'left' }}>
                                                                                            Outcomes Summary Table
                                                                                            <span style={{ float: 'right' }}>
                                                                                                <DropdownWithIcons
                                                                                                    modalRef={contractBoardsOutcomesSummaryRef}
                                                                                                    kpiName={"Contract Boards Outcomes Summary"}
                                                                                                    isTable={true}
                                                                                                    checkPDFDownloaded={checkPDFDownloaded}
                                                                                                />
                                                                                            </span>
                                                                                        </div>

                                                                                

                                                                                        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                                                                            <span className="cf-outcomes-summary-legend-label">Legend</span>
                                                                                            {
                                                                                                contractBoardsOutcomesSummaryLegendItems.map((legendItem) => (
                                                                                                    <span
                                                                                                        key={legendItem.label}
                                                                                                        style={{
                                                                                                            display: 'inline-block',
                                                                                                            padding: '4px 10px',
                                                                                                            borderRadius: '5px',
                                                                                                            background: legendItem.color,
                                                                                                            border: `1px solid ${legendItem.strokeColor}`,
                                                                                                            color: '#111111',
                                                                                                            // fontSize: '12px',
                                                                                                            // lineHeight: 1.2,
                                                                                                            whiteSpace: 'nowrap',
                                                                                                        }}
                                                                                                        className="cf-outcomes-summary-legend-label"
                                                                                                    >
                                                                                                        {legendItem.label}
                                                                                                    </span>
                                                                                                ))
                                                                                            }
                                                                                        </div>

                                                                                        <div style={{ width: '100%' }}>
                                                                                            {
                                                                                                categoryForumOutcomeSummaryTabularData && categoryForumOutcomeSummaryTabularData?.tabularDetails?.length > 0 ?
                                                                                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%' }}>
                                                                                                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', overflow: 'hidden' }}>
                                                                                                            <div 
                                                                                                            className="category-forums-contract-boarddetails-horizontal-scroll-outcome-summary" 
                                                                                                            style={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                                                                                                                <DataTable
                                                                                                                    className="category-forums-action-items-table" 
                                                                                                                    columns={contractBoardsOutcomesSummaryTableColumns}
                                                                                                                    data={categoryForumOutcomeSummaryTabularData?.tabularDetails}
                                                                                                                    isExpandable={false}
                                                                                                                    enableSorting={true}
                                                                                                                    pageIndex={0}
                                                                                                                    pageSize={10}
                                                                                                                    size="large"
                                                                                                                    tableDescription={false}
                                                                                                                    tableHeader={false}
                                                                                                                    showSearchOption={false}
                                                                                                                    showColumnFilters={false}
                                                                                                                />
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    : <NoData />
                                                                                            }
                                                                                        </div>

                                                                                    </Card>
                                                                                </>
                                                                            )
                                                                        }
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </>
                                                    )
                                                }

                                                {
                                                    CategoryForumsSwitcherIndex == actionTrackerTabIndex && (
                                                        <>
                                                            <div ref={contractBoardsActionTrackerRef}>
                                                                {/* Description Section */}
                                                                <div className="category-forums-description-title" style={{ width: '100%' }}>
                                                                    Contract Boards Action Tracker
                                                                    <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                                        <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={categoryForumsContractBoardRef} kpiName={"Contract Boards Action Tracker"} isModal={true} />
                                                                    </span>
                                                                </div>
                                                                <div className="forums-description-text" style={{ marginBottom: '1rem' }}>Status of Action Items aligned during Contract Board meetings.</div>

                                                                
                                                                <div style={{ width: "100%", marginTop: "12px" }}>
                                                                    <Row
                                                                        gutter={[10, 0]}
                                                                        justify="start"
                                                                        wrap={false}
                                                                        className="category-forums-action-tracker-top-filter-row"
                                                                        style={{
                                                                            width: "100%",
                                                                            alignItems: "flex-end",
                                                                            flexWrap: "nowrap"
                                                                        }}
                                                                    >
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Sector"
                                                                                placeholder="Sectors"
                                                                                childList={actionTrackerSectorOptions}
                                                                                selectedValue={actionTrackerSectorSelected}
                                                                                setSelectedValue={onActionTrackerSectorChange}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Category L0"
                                                                                placeholder="Categories L0"
                                                                                childList={actionTrackerCategoryL0Options}
                                                                                selectedValue={actionTrackerCategoryL0Selected}
                                                                                setSelectedValue={onActionTrackerCategoryL0Change}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Category L1"
                                                                                placeholder="Categories L1"
                                                                                childList={actionTrackerCategoryL1Options}
                                                                                selectedValue={actionTrackerCategoryL1Selected}
                                                                                setSelectedValue={onActionTrackerCategoryL1Change}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Action Items Owner"
                                                                                placeholder="Action Items Owners"
                                                                                childList={actionTrackerOwnerOptions}
                                                                                selectedValue={actionTrackerOwnerSelected}
                                                                                setSelectedValue={onActionTrackerOwnerChange}
                                                                            />
                                                                        </Col>
                                                                        <Col flex="1">
                                                                            <MultiSelectDropdown
                                                                                className="buy-con-dropdown-modal"
                                                                                label="Action Items Status"
                                                                                placeholder="Action Items Statuses"
                                                                                childList={actionTrackerStatusOptions}
                                                                                selectedValue={actionTrackerStatusSelected}
                                                                                setSelectedValue={onActionTrackerStatusChange}
                                                                            />
                                                                        </Col>
                                                                         
                                                                    </Row>
                                                                    <Row justify="end">
                                                                        <button className="reset-button" onClick={onActionTrackerFiltersReset} style={{ marginTop: '12px', marginRight: '0.8rem' }}>
                                                                            <img src={reset_icon} className="reset_icon" alt="Reset Icon" />
                                                                            <span>Reset Filters</span>
                                                                        </button>
                                                                    </Row>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', gap: '1rem', marginBottom: '0.5rem' }}>
                                                                    <span style={{ position: 'relative', top: '7px' }} className="category-forums-kpi-section-title"></span>
                                                                    <span>
                                                                        <Switcher
                                                                            items={[
                                                                                { label: String(new Date().getFullYear() - 1) },
                                                                                { label: String(new Date().getFullYear()) },
                                                                                { label: String(new Date().getFullYear() + 1) },
                                                                            ]}
                                                                            selectedItemIndex={actionTrackerSelectedYear - (new Date().getFullYear() - 1)}
                                                                            onUpdate={(idx) => setActionTrackerSelectedYear(new Date().getFullYear() - 1 + idx)}
                                                                        />
                                                                    </span>
                                                                </div>

                                                                {
                                                                    isActionTrackerKpiLoading ? (
                                                                        <>
                                                                            <PHKPICard
                                                                                slotContent={<SkeletonList count={3} height={26} />}
                                                                                className={"vr-card"}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="new-forum-card category-forums-kpi-card">
                                                                                <div className="category-forums-kpi-section">
                                                                                    <div className="category-forums-kpi-row category-forums-kpi-row-equal">
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{actionTrackerContractForumsCompletedCount}</div>
                                                                                                <div className="category-forums-kpi-stat-label">Contract Board Forums completed YTD</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{formatNumberWithCommas(totalActionItemsCount ?? 0)}</div>
                                                                                                <div className="category-forums-kpi-stat-label">Total Action Items</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="category-forums-kpi-section-column">
                                                                                            <div className="category-forums-kpi-stat-card">
                                                                                                <div className="category-forums-kpi-stat-number">{completedActionItemsCount}</div>
                                                                                                <div className="category-forums-kpi-stat-label">Action Items Completed</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )
                                                                }

                                                                <Row gutter={[10, 10]} justify={"start"} style={{ position: 'relative' }}>
                                                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                                                        <div ref={actionTrackerStatusRef} style={{ height: '100%' }}>
                                                                            {
                                                                                isActionTrackerChartsLoading ? (
                                                                                    <>
                                                                                        <PHKPICard
                                                                                            slotContent={<SkeletonList count={13} height="30" />}
                                                                                            className={"vr-card"}
                                                                                        />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Card style={{ height: '100%' }} className="archlet-chart-card">
                                                                                            <div className="category-forums-kpi-section-title" style={{ width: "100%", textAlign: "left" }}>
                                                                                                Action Items status
                                                                                                <span style={{ float: 'right' }}>
                                                                                                    <DropdownWithIcons
                                                                                                        modalRef={actionTrackerStatusRef} kpiName={"Action Items status"}
                                                                                                        checkPDFDownloaded={checkPDFDownloaded}
                                                                                                    />
                                                                                                </span>
                                                                                            </div>
                                                                                            {
                                                                                                ActionTrackerDonutChartData && ActionTrackerDonutChartData?.length > 0 ?
                                                                                                    <>
                                                                                                        {
                                                                                                            <>
                                                                                                                <SpendDonutChart countryData={ActionTrackerDonutChartData} isCategoryForums={true} />
                                                                                                            </>
                                                                                                        }
                                                                                                    </>
                                                                                                    :
                                                                                                    <NoData />
                                                                                            }
                                                                                        </Card>
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </Col>

                                                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                                                        <div ref={actionTrackerForumsByMonthRef}>
                                                                            {
                                                                                isActionTrackerChartsLoading ? (
                                                                                    <>
                                                                                        <PHKPICard
                                                                                            slotContent={<SkeletonList count={13} height="30" />}
                                                                                            className={"vr-card"}
                                                                                        />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Card style={{ height: '100%' }} className="archlet-chart-card">
                                                                                            <div className="category-forums-kpi-section-title" style={{ width: "100%", textAlign: "left" }}>
                                                                                                Action Items status by month
                                                                                                <span style={{ float: 'right' }}>
                                                                                                    <DropdownWithIcons
                                                                                                        modalRef={actionTrackerForumsByMonthRef} kpiName={"Action Items status by month"}
                                                                                                        checkPDFDownloaded={checkPDFDownloaded}
                                                                                                    />
                                                                                                </span>
                                                                                            </div>
                                                                                            {
                                                                                                actionTrackerBarChartData?.length > 0 && processedActionTrackerBarChartLegends?.length > 0 ?
                                                                                                    <>
                                                                                                        <>
                                                                                                            <CategoryForumsBarChart linebarchartData={actionTrackerBarChartData} processedforumsByMonthBarChartLegends={processedActionTrackerBarChartLegends} />
                                                                                                            {renderLegendMarkup(processedActionTrackerBarChartLegends, "contractBoard")}
                                                                                                        </>
                                                                                                    </>
                                                                                                    :
                                                                                                    <NoData />
                                                                                            }
                                                                                        </Card>
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                                <Row gutter={[10, 10]} style={{ width: '100%' }}>
                                                                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                                                        {
                                                                            isActionTrackerTableLoading ? (
                                                                                <>
                                                                                    <PHKPICard
                                                                                        slotContent={<SkeletonList count={8} height="30" />}
                                                                                        className={"vr-card"}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Card ref={contractBoardsActionItemsTableRef} style={{ height: '100%', marginTop: '1rem', minHeight: '360px' }} className="category-forums-action-items-card">
                                                                                        <div className="category-forums-action-items-card-header" style={{ width: '100%', textAlign: 'left', marginBottom: '20px' }}>
                                                                                            Contract Boards Action Items Details
                                                                                            <span style={{ float: 'right' }}>
                                                                                                <DropdownWithIcons
                                                                                                    modalRef={contractBoardsActionItemsTableRef}
                                                                                                    kpiName={"Contract Boards Action Items"}
                                                                                                    isTable={true}
                                                                                                    checkPDFDownloaded={checkPDFDownloaded}
                                                                                                />
                                                                                            </span>
                                                                                        </div>

                                                                                        <div className="category-forums-action-items-card-filters" style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                                                                            <Row gutter={[0, 0]} justify="start" style={{ width: "100%", gap: "8px" }}>
                                                                                                <Col flex="0 0 240px">
                                                                                                    <SingleSelectDropdown
                                                                                                        className='buy-con-dropdown-modal'
                                                                                                        label={"From Month"}
                                                                                                        childList={actionTrackerFromDateOptions}
                                                                                                        selectedValue={actionTrackerFromDateSelected}
                                                                                                        setSelectedValue={onActionTrackerFromDateChange}
                                                                                                    />
                                                                                                </Col>
                                                                                                <Col flex="0 0 240px">
                                                                                                    <SingleSelectDropdown
                                                                                                        className='buy-con-dropdown-modal'
                                                                                                        label="To Month"
                                                                                                        childList={actionTrackerToDateOptions}
                                                                                                        selectedValue={actionTrackerToDateSelected}
                                                                                                        setSelectedValue={onActionTrackerToDateChange}
                                                                                                    />
                                                                                                </Col>
                                                                                                <Col flex="0 0 auto">
                                                                                                    <button className="reset-button" onClick={onActionTrackerDateFiltersReset} style={{ marginTop: '20px', marginRight: '0.8rem', marginBottom: '0px' }}>
                                                                                                        <img src={reset_icon} className="reset_icon" alt="Reset Icon" />
                                                                                                        <span>Reset Filters</span>
                                                                                                    </button>
                                                                                                </Col>
                                                                                            </Row>
                                                                                        </div>

                                                                                        <div style={{ width: '100%' }}>
                                                                                            {
                                                                                                actionTrackerDisplayTableData && actionTrackerDisplayTableData?.length > 0 ?
                                                                                                    <div className="category-forums-contract-boarddetails-horizontal-scroll">
                                                                                                        <DataTable
                                                                                                            className="category-forums-action-items-table" columns={contractBoardsActionItemsColumns}
                                                                                                            data={actionTrackerDisplayTableData}
                                                                                                            isExpandable={false}
                                                                                                            enableSorting={true}
                                                                                                            pageIndex={0}
                                                                                                            pageSize={10}
                                                                                                            size="large"
                                                                                                            tableDescription={false}
                                                                                                            tableHeader={false}
                                                                                                            showSearchOption={false}
                                                                                                            showColumnFilters={false}
                                                                                                        />
                                                                                                    </div>
                                                                                                    : <NoData />
                                                                                            }
                                                                                        </div>

                                                                                    </Card>
                                                                                </>
                                                                            )
                                                                        }
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </>
                                                    )
                                                }



                                            </div>

                                            <br />
                                            <br />

                                            {renderFooterSection(staticOwnersData, feedbackFormUrl)}
                                            <br />
                                            <br />

                                        </div>


                                    </div>
                                )}
                            </>
                        )}
                    </Content>
                </div>
            </Layout>
        );
    }

    return null;
}

export default CategoryForums;